import { lazyRouteComponent } from '@tanstack/react-router';
import type { AnyRoute } from '@tanstack/react-router';

// Route-specific components defined via file-based routes (keep static)
import { Route as FamousEDIShipmentDetailRoute } from 'src/pages/authenticated/famous-edi-shipments/$shipmentId';
import { Route as OrderConfirmationDetailRoute } from 'src/pages/authenticated/famous-edi-order-confirmation/$orderConfirmationId';
import { Route as PriceQuoteSheetDashboardDetailRoute } from './pages/authenticated/price-sheet-dashboard/detail.$quoteSheetId';
import { Route as PriceQuoteSheetInboxDetailRoute } from './pages/authenticated/price-sheet-inbox/$quoteSheetId';
import { Route as memoryBankNotesRoute } from './pages/authenticated/memory-bank-notes';
import { Route as guardrailPoliciesRoute } from './pages/authenticated/guardrail-policies';
import { Route as integrationsListRoute } from './pages/authenticated/integrations';
import { Route as auditReportsRoute } from './pages/authenticated/audit-reports';
// AR Vision Demo (GEN-2247) — throwaway demo-only route. Delete with src/demo/ar.
import { Route as demoArWorkflowRoute } from './demo/ar/route';
// AP Invoices Demo — throwaway demo-only "build an AP workflow" route. Delete
// with src/demo/ap.
import { Route as demoApWorkflowRoute } from './demo/ap/workflow/route';
import { Route as notificationsHistoryRoute } from './pages/authenticated/notifications';
import { Route as integrationDetailRoute } from './pages/authenticated/integrations/$integrationKey';

// Lazy page components — only loaded when route is visited
// Demo: the Customers tab shows the v100 dummy-customer list (100 records).
const CustomersPage = lazyRouteComponent(
  () => import('src/pages/authenticated/sales-orders-v100/CustomersV100Page'),
);
const SalesOrderDraftsPage = lazyRouteComponent(
  () => import('src/pages/authenticated/sales-orders/SalesOrderDraftsPage'),
);
const SalesOrdersV3Page = lazyRouteComponent(
  () => import('src/pages/authenticated/sales-orders-v3/SalesOrdersV3Page'),
);
const SalesOrderV3DetailPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/sales-orders-v3/$draftId/SalesOrderV3DetailPage'),
);
// Sales Order v100 — AI-first OMS ("Nami") demo. Native React port of the
// docs/oms-demo prototype; all dummy data, demo branch only.
const SalesOrdersV100Page = lazyRouteComponent(
  () => import('src/pages/authenticated/sales-orders-v100/SalesOrdersV100Page'),
  'SalesOrdersV100Page',
);
const SalesOrderV100ReviewPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/sales-orders-v100/$orderId/SalesOrderV100ReviewPage'),
  'SalesOrderV100ReviewPage',
);
const AdminCanvasPage = lazyRouteComponent(
  () => import('src/pages/authenticated/sales-orders-v100/AdminCanvasPage'),
  'AdminCanvasPage',
);
const EnhancedSalesOrderDraftPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/sales-orders/$salesOrderDraftId/EnhancedSalesOrderDraftPage'),
  'EnhancedSalesOrderDraftPage',
);
const SalesOrderPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/sales-orders/$salesOrderId/SalesOrderPage'),
);
const OrderMetricsDashboard = lazyRouteComponent(
  () => import('src/pages/authenticated/order-metrics/OrderMetricsDashboard'),
);
const SalesReportsPage = lazyRouteComponent(
  () => import('src/pages/authenticated/sales-reports/SalesReportsPage'),
);
const InboundEmailsPage = lazyRouteComponent(
  () => import('src/pages/authenticated/inbound-emails/InboundEmailsPage'),
);
const InboundEmailRulesPage = lazyRouteComponent(
  () => import('src/pages/authenticated/inbound-emails/InboundEmailRulesPage'),
  'InboundEmailRulesPage',
);
const DocumentsPage = lazyRouteComponent(
  () => import('src/pages/authenticated/documents/DocumentsPage'),
);
const DocumentDetailPage = lazyRouteComponent(
  () => import('src/pages/authenticated/documents/DocumentDetailPage'),
);
const FileExtractionPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/tools/file-extraction/FileExtractionPage'),
  'FileExtractionPage',
);

const FamousEDIPage = lazyRouteComponent(
  () => import('src/pages/authenticated/famous-edi/FamousEDIPage'),
);
const FamousEDIShipmentsPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/famous-edi-shipments/FamousEDIShipmentsPage'),
);
const FamousEDIOrderConfirmationPage = lazyRouteComponent(
  () =>
    import('src/pages/authenticated/famous-edi-order-confirmation/FamousEDIOrderConfirmationPage'),
);
const FamousEdi855Page = lazyRouteComponent(
  () => import('src/pages/authenticated/famous-edi-855/FamousEdi855Page'),
);
const EdiTextMessagesPage = lazyRouteComponent(
  () => import('src/pages/authenticated/edi-text-messages/EdiTextMessagesPage'),
);

const InboxPage = lazyRouteComponent(
  () => import('src/pages/authenticated/inbox/InboxPage'),
);
const InboxErrorComponent = lazyRouteComponent(
  () => import('src/pages/authenticated/inbox/InboxPage'),
  'InboxErrorComponent',
);
import { inboxSearchSchema } from 'src/pages/authenticated/inbox/hooks/use-inbox-search-params';

const RebatesAnalyticsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/forecasting/RebatesAnalyticsPage'),
);
const RebateDemoAnalyticsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/demo-analytics/RebateDemoAnalyticsPage'),
);
const SupplierCollectionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/supplier-collections/SupplierCollectionsPage'),
);
const RebateAgreementsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/agreements/RebateAgreementsPage'),
);
const RebateAgreementDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/agreements/RebateAgreementDetailPage'),
);
const RebateAuditsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/audits/RebateAuditsPage'),
);
const RebateAuditDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/audits/RebateAuditDetailPage'),
);
const SupplierReportedAmountsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/supplier-reported-amounts/SupplierReportedAmountsPage'),
);
const RebateCalculationAuditPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/calculation-audit/RebateCalculationAuditPage'),
);
const RebateTierProximityPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/tier-proximity/RebateTierProximityPage'),
  'RebateTierProximityPage',
);
const ConvertDataPageWorkflow = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/ConvertDataPageWorkflow'),
);
const DataUploadRouter = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/upload-data/DataUploadRouter'),
);
const ManualRebateCSVUpload = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/ManualRebateCSVUpload'),
);
const RebateCsvTransformJobsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/RebateCsvTransformJobsPage'),
  'RebateCsvTransformJobsPage',
);
const RebateCsvTransformRunPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/RebateCsvTransformRunPage'),
);
const DemoResultsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/upload-data/DemoResultsPage'),
);
const EmailUploadQueuePage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/upload-data/EmailUploadQueuePage'),
);
const DivisionsPage = lazyRouteComponent(
  () => import('./pages/authenticated/divisions/DivisionsPage'),
);
const DivisionDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/divisions/DivisionDetailPage'),
);
const PriceQuoteSheetDashboard = lazyRouteComponent(
  () =>
    import('./pages/authenticated/price-sheet-dashboard/PriceQuoteSheetDashboard'),
  'PriceQuoteSheetDashboard',
);
const PriceQuoteSheetInboxPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/price-sheet-inbox/PriceQuoteSheetInboxPage'),
  'PriceQuoteSheetInboxPage',
);
const WarehousesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/inventory-management/warehouses/WarehousesPage'),
  'WarehousesPage',
);
const ItemCatalogPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/ItemCatalogPage'),
);
const PricelistManagementPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/PricelistManagementPage'),
  'PricelistManagementPage',
);
const ReviewsPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/ReviewsPage'),
  'ReviewsPage',
);
const ReviewDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/item-master/ReviewDetailPage'),
  'ReviewDetailPage',
);

const WarehouseDetailRoute = lazyRouteComponent(
  () =>
    import('./pages/authenticated/inventory-management/warehouses/$warehouseId'),
  'WarehouseDetailRoute',
);
const ReplenishmentPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/replenishment/ReplenishmentPage'),
  'ReplenishmentPage',
);
const SupplierDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/replenishment/suppliers/SupplierDetailPage'),
  'SupplierDetailPage',
);
const SalesOrderStatusPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/sales-order-status/SalesOrderStatusPage'),
  'SalesOrderStatusPage',
);
const TransferOrderStatusPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/transfer-order-status/TransferOrderStatusPage'),
  'TransferOrderStatusPage',
);
const AS400RawDataPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/as400-raw-data/AS400RawDataPage'),
  'AS400RawDataPage',
);
const QueryBuilderPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/query-builder/QueryBuilderPage'),
  'QueryBuilderPage',
);
const QueryBuilderPrintPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/query-builder/QueryBuilderPrintPage'),
  'QueryBuilderPrintPage',
);
const ObsoleteProductsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/obsolete-products/ObsoleteProductsPage'),
  'ObsoleteProductsPage',
);
const InventoryPage = lazyRouteComponent(
  () => import('./pages/authenticated/procurement/inventory/InventoryPage'),
  'InventoryPage',
);
const ProductDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/product-detail/ProductDetailPage'),
  'ProductDetailPage',
);
const SupplierProductsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/supplier-products/SupplierProductsPage'),
  'SupplierProductsPage',
);
// DEMO: real Purchase Order Status page intentionally not routed — hidden
// behind the canned DemoPurchaseOrderStatusPage below.
const ReplenishmentOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/replenishment/ReplenishmentOrdersPage'),
  'ReplenishmentOrdersPage',
);
// DEMO: the replenishment item pages (one item of open demand, with its fill
// options); reached from the Replenishment page below.
const DemandRadarItemDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/procurement/demo/ItemDetailPage'),
  'ItemDetailPage',
);
// DEMO: canned Purchase Order Status page that hides the real one (see
// procurementPurchaseOrdersRoute) for the procurement demo.
const DemoPurchaseOrderStatusPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/procurement/demo/PurchaseOrderStatusPage'),
  'PurchaseOrderStatusPage',
);
// DEMO: Bausch + Lomb procurement suite (frontend/src/demo/procurement, see README).
const ProcurementOverviewPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/overview/OverviewPage'),
  'ProcurementOverviewPage',
);
const ProcurementRequestsPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/requests/RequestsPage'),
  'ProcurementRequestsPage',
);
const ProcurementSuppliersPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/suppliers/SuppliersPage'),
  'ProcurementSuppliersPage',
);
const ProcurementSupplierDetailPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/suppliers/SupplierDetailPage'),
  'ProcurementSupplierDetailPage',
);
const ProcurementMaterialComparePage = lazyRouteComponent(
  () => import('./demo/procurement/pages/suppliers/MaterialComparePage'),
  'ProcurementMaterialComparePage',
);
const ProcurementOnboardingCasePage = lazyRouteComponent(
  () => import('./demo/procurement/pages/suppliers/OnboardingCasePage'),
  'ProcurementOnboardingCasePage',
);
const ProcurementSourcingPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/sourcing/SourcingPage'),
  'ProcurementSourcingPage',
);
const ProcurementSourcingEventPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/sourcing/SourcingEventPage'),
  'ProcurementSourcingEventPage',
);
const ProcurementContractsPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/contracts/ContractsPage'),
  'ProcurementContractsPage',
);
const ProcurementContractDetailPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/contracts/ContractDetailPage'),
  'ProcurementContractDetailPage',
);
const ProcurementReplenishmentPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/buyer-board/ReplenishmentPage'),
  'ProcurementReplenishmentPage',
);
const ProcurementInvoiceCompliancePage = lazyRouteComponent(
  () => import('./demo/procurement/pages/compliance/InvoiceCompliancePage'),
  'ProcurementInvoiceCompliancePage',
);
const ProcurementForecastSummaryPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/forecast/ForecastSummaryPage'),
  'ProcurementForecastSummaryPage',
);
const ProcurementForecastDetailsPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/forecast/ForecastDetailsPage'),
  'ProcurementForecastDetailsPage',
);
const ProcurementBomForecastPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/forecast/BomForecastPage'),
  'ProcurementBomForecastPage',
);
const ProcurementAgentsPage = lazyRouteComponent(
  () => import('./demo/procurement/pages/agents/AgentsPage'),
  'ProcurementAgentsPage',
);
import { Route as ProcurementPurchaseOrderDetailRoute } from './pages/authenticated/procurement/purchase-orders/$purchaseOrderId';
const FeatureFlagsPage = lazyRouteComponent(
  () => import('./pages/authenticated/ff_overrides/FeatureFlagsPage'),
  'FeatureFlagsPage',
);
const HistoricalImportPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/admin/historical-import/HistoricalImportPage'),
  'HistoricalImportPage',
);
const BackfillsPage = lazyRouteComponent(
  () => import('./pages/authenticated/admin/backfills/BackfillsPage'),
  'BackfillsPage',
);
const RexelMonitoringPage = lazyRouteComponent(
  () => import('./pages/authenticated/rexel-monitoring/RexelMonitoringPage'),
);

const ShipAndDebitDashboard = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/dashboard/ShipAndDebitDashboard'),
  'ShipAndDebitDashboard',
);
const ExecutiveSummaryPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/executive-summary/ExecutiveSummaryPage'),
  'ExecutiveSummaryPage',
);
const VDMsPage = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/vdms/VDMsPage'),
  'VDMsPage',
);
const VDMDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/vdms/VDMDetailPage'),
  'VDMDetailPage',
);
const Staged844TransactionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/staged-844/Staged844TransactionsPage'),
  'Staged844TransactionsPage',
);
const ShipAndDebit844sPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/844s/ShipAndDebit844sPage'),
  'ShipAndDebit844sPage',
);
const NoClaimTransactionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/no-claim-transactions/NoClaimTransactionsPage'),
  'NoClaimTransactionsPage',
);
const StagedInternalTransactionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/staged-internal/StagedInternalTransactionsPage'),
  'StagedInternalTransactionsPage',
);
const ShipAndDebit849ResponsesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/849s/ShipAndDebit849ResponsesPage'),
  'ShipAndDebit849ResponsesPage',
);
const ShipAndDebit849ResponseDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/849s/ShipAndDebit849ResponseDetailPage'),
  'ShipAndDebit849ResponseDetailPage',
);
const SearchTransactionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/search-transactions/SearchTransactionsPage'),
  'SearchTransactionsPage',
);
const ActivityLogPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/activity-log/ActivityLogPage'),
  'ActivityLogPage',
);
const Search844Page = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/search-844/Search844Page'),
  'Search844Page',
);
const CompanyLocationsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/company-locations/CompanyLocationsPage'),
  'CompanyLocationsPage',
);
const RexelFilesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/rexel-files/RexelFilesPage'),
  'RexelFilesPage',
);
const AdminReportsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/admin-reports/AdminReportsPage'),
  'AdminReportsPage',
);
const AgentConfigPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/agent-config/AgentConfigPage'),
  'AgentConfigPage',
);
// AI-first Ship & Debit demo module (frontend-only, mock data). Replaces the
// legacy S&D sections above (which are hidden from nav).
const SdDemoDashboard = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/demo/DashboardPage'),
  'ShipAndDebitDemoDashboard',
);
const SdDemoPrograms = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/demo/ProgramsPage'),
  'ProgramsPage',
);
const SdDemoClaimsReview = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/demo/ClaimsReviewPage'),
  'ClaimsReviewPage',
);
const SdDemoClaimsSubmission = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/demo/ClaimsSubmissionPage'),
  'ClaimsSubmissionPage',
);
const SdDemoVendorResponses = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/demo/VendorResponsesPage'),
  'VendorResponsesPage',
);
const SdDemoCollections = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/demo/CollectionsPage'),
  'CollectionsPage',
);
const SdDemoRevenueLeakage = lazyRouteComponent(
  () => import('./pages/authenticated/ship-and-debit/demo/RevenueLeakagePage'),
  'RevenueLeakagePage',
);
const SdDemoVendorManagement = lazyRouteComponent(
  () =>
    import('./pages/authenticated/ship-and-debit/demo/VendorManagementPage'),
  'VendorManagementPage',
);
const WorkflowTemplatesPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowTemplatesPage'),
  'WorkflowTemplatesPage',
);
const WorkflowsPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowsPage'),
  'WorkflowsPage',
);
const WorkflowStepTesterPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/step-tester/WorkflowStepTesterPage'),
  'WorkflowStepTesterPage',
);
const WorkflowTrafficGeneratorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/traffic-generator/WorkflowTrafficGeneratorPage'),
  'WorkflowTrafficGeneratorPage',
);
const WorkflowDefinitionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionsTab'),
  'WorkflowDefinitionsTab',
);
const WorkflowDefinitionLayoutPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionLayoutPage'),
  'WorkflowDefinitionLayoutPage',
);
const WorkflowDefinitionOverviewTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionOverviewTab'),
  'WorkflowDefinitionOverviewTab',
);
const WorkflowDefinitionStepsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionStepsTab'),
  'WorkflowDefinitionStepsTab',
);

const WorkflowDefinitionVersionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionVersionsTab'),
  'WorkflowDefinitionVersionsTab',
);
const WorkflowDefinitionRunsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionRunsTab'),
  'WorkflowDefinitionRunsTab',
);
const WorkflowDefinitionTestTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowDefinitionTestTab'),
  'WorkflowDefinitionTestTab',
);
const WorkflowAnalyticsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowAnalyticsTab'),
  'WorkflowAnalyticsTab',
);
const WorkflowApiTokensTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowApiTokensTab'),
  'WorkflowApiTokensTab',
);
const WorkflowManagerAgentTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowManagerAgentTab'),
  'WorkflowManagerAgentTab',
);
const WorkflowRunDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowRunDetailPage'),
  'WorkflowRunDetailPage',
);

const BackgroundAgentsPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/BackgroundAgentsPage'),
  'BackgroundAgentsPage',
);
const BackgroundAgentsListingPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/BackgroundAgentsListingPage'),
  'BackgroundAgentsListingPage',
);
const BackgroundAgentTemplatesPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/BackgroundAgentTemplatesPage'),
  'BackgroundAgentTemplatesPage',
);
const AgentDefinitionLayoutPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionLayoutPage'),
  'AgentDefinitionLayoutPage',
);
const AgentDefinitionAgentTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionAgentTab'),
  'AgentDefinitionAgentTab',
);
const AgentDefinitionToolsTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionToolsTab'),
  'AgentDefinitionToolsTab',
);
const AgentDefinitionTaskManagementTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionTaskManagementTab'),
  'AgentDefinitionTaskManagementTab',
);
const AgentDefinitionResourcesTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionResourcesTab'),
  'AgentDefinitionResourcesTab',
);
const AgentDefinitionPermissionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionPermissionsTab'),
  'AgentDefinitionPermissionsTab',
);
const AgentDefinitionSessionsTab = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionSessionsTab'),
  'AgentDefinitionSessionsTab',
);
const AgentDefinitionMaterializeTab = lazyRouteComponent(
  () =>
    import('./pages/authenticated/agents/materialize/AgentDefinitionMaterializeTab'),
  'AgentDefinitionMaterializeTab',
);
const AgentDefinitionSessionsLayout = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentDefinitionSessionsLayout'),
  'AgentDefinitionSessionsLayout',
);
const AgentRunDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/agents/AgentRunDetailPage'),
  'AgentRunDetailPage',
);

const AgentMonitorListingPage = lazyRouteComponent(
  () => import('./pages/authenticated/agent-monitor/AgentMonitorListingPage'),
  'AgentMonitorListingPage',
);
const AgentMonitorPage = lazyRouteComponent(
  () => import('./pages/authenticated/agent-monitor/AgentMonitorPage'),
  'AgentMonitorPage',
);
const DataAssetsPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/data-assets/DataAssetsPage'),
  'DataAssetsPage',
);
const StatefulTablesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/stateful-tables/StatefulTablesPage'),
  'StatefulTablesPage',
);
const StatefulTableDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/workflows/stateful-tables/StatefulTableDetailPage'),
  'StatefulTableDetailPage',
);

const WorkflowGroupsTab = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowGroupsTab'),
  'WorkflowGroupsTab',
);
const WorkflowGroupEditorPage = lazyRouteComponent(
  () => import('./pages/authenticated/workflows/WorkflowGroupEditorPage'),
  'WorkflowGroupEditorPage',
);
import {
  ReceiptIcon,
  ClipboardTextIcon,
  TagIcon,
  ArrowCounterClockwiseIcon,
  TruckIcon,
  PresentationChartIcon,
  BuildingIcon,
  BuildingsIcon,
  TableIcon,
  CurrencyDollarIcon,
  FileTextIcon,
  ShoppingCartIcon,
  BankIcon,
  TrendUpIcon,
  PackageIcon,
  TargetIcon,
  ChartBarIcon,
  PercentIcon,
  DownloadIcon,
  ClockCounterClockwiseIcon,
  ArrowsLeftRightIcon,
  MonitorIcon,
  EnvelopeSimpleIcon,
  TrashIcon,
  GearIcon,
  UsersIcon,
  MagnifyingGlassIcon,
  DatabaseIcon,
  TrayIcon,
  RobotIcon,
  GaugeIcon,
  FolderOpenIcon,
  PaperPlaneTiltIcon,
  ChatCenteredDotsIcon,
  ClockCountdownIcon,
  TrendDownIcon,
  HandshakeIcon,
  SparkleIcon,
  ChartPieSliceIcon,
  GavelIcon,
  SignatureIcon,
  KanbanIcon,
  ShieldCheckIcon,
  ChartLineUpIcon,
} from '@phosphor-icons/react';
import { slugify } from 'src/utils/strings';
import { Action, Subject } from '@erp/authz';

import { Route as rootRoute, type RouterContext } from 'src/pages/__root';
import { createRoute, redirect, Outlet } from '@tanstack/react-router';
const StatusPage = lazyRouteComponent(
  () => import('@/pages/(unauthenticated)/status/StatusPage'),
);
const TeamsTabPage = lazyRouteComponent(
  () => import('@/pages/(unauthenticated)/teams-tab/TeamsTabPage'),
);
import { Route as loginRoute } from 'src/pages/(unauthenticated)/login';
import { Route as forgotPasswordRoute } from 'src/pages/(unauthenticated)/forgot-password';
import { Route as resetPasswordRoute } from 'src/pages/(unauthenticated)/reset-password';
import { Route as workosCallbackRoute } from 'src/pages/(unauthenticated)/workos-callback';
import { Route as googleCallbackRoute } from 'src/pages/(unauthenticated)/google-callback';
import { portalLoginRoute } from 'src/pages/portal/login';
import { portalAuthenticatedRoute } from 'src/pages/portal/route';
import { portalDashboardRoute } from 'src/pages/portal/index';
import { Route as indexRoute } from 'src/pages/index';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requireAdmin,
  requirePermission,
  requireAnyPermission,
} from 'src/pages/authenticated/route';
const SettingsOrganizationLayoutPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/SettingsOrganizationPage'),
  'SettingsOrganizationLayoutPage',
);
const SettingsOrganizationSectionPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/SettingsOrganizationSectionPage'),
  'SettingsOrganizationSectionPage',
);
import {
  DEFAULT_ORG_SECTION_ID,
  LEGACY_SECTION_SLUG_MAP,
  ORGANIZATION_SETTINGS_SECTIONS,
  OrganizationSettingsSectionId,
  sectionPath,
} from './pages/authenticated/settings/organization/organizationSettingsSections';
// Integration Marketplace Demo (?demo=im) — lets the gently-only Data Sources
// section be reached in demo mode. Delete with frontend/src/demo/im.
import { isImDemoMode } from './demo/im/demo-mode';
const ControlPlaneLinkPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/control-plane-link/ControlPlaneLinkPage'),
  'ControlPlaneLinkPage',
);
const UserPreferencesPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/UserPreferencesPage'),
  'UserPreferencesPage',
);
const WelcomePage = lazyRouteComponent(
  () => import('./pages/authenticated/WelcomePage'),
  'WelcomePage',
);
const TenantPage = lazyRouteComponent(
  () => import('./pages/authenticated/tenant/TenantPage'),
  'TenantPage',
);
const OutOfOfficePage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/OutOfOfficePage'),
  'OutOfOfficePage',
);

const SecurityPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/SecurityPage'),
  'SecurityPage',
);

const NavigationSettingsPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/NavigationSettingsPage'),
  'NavigationSettingsPage',
);
const NotificationSettingsPage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/NotificationSettingsPage'),
  'NotificationSettingsPage',
);
const EmailSignaturePage = lazyRouteComponent(
  () => import('./pages/authenticated/settings/user/EmailSignaturePage'),
  'EmailSignaturePage',
);
const EnumAssociationsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/core-enums/EnumAssociationsPage'),
  'EnumAssociationsPage',
);
const EmailTemplateEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/email-templates/EmailTemplateEditorPage'),
  'EmailTemplateEditorPage',
);
const EmailTemplateVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/email-templates/EmailTemplateVersionsPage'),
  'EmailTemplateVersionsPage',
);
const PdfTemplateEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/pdf-templates/PdfTemplateEditorPage'),
  'PdfTemplateEditorPage',
);
const PdfTemplateVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/pdf-templates/PdfTemplateVersionsPage'),
  'PdfTemplateVersionsPage',
);
const InternalUiTemplateEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/internal-ui-templates/InternalUiTemplateEditorPage'),
  'InternalUiTemplateEditorPage',
);
const InternalUiTemplateVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/internal-ui-templates/InternalUiTemplateVersionsPage'),
  'InternalUiTemplateVersionsPage',
);
const TemplateBlockEditorPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/template-blocks/TemplateBlockEditorPage'),
  'TemplateBlockEditorPage',
);
const TemplateBlockVersionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/template-blocks/TemplateBlockVersionsPage'),
  'TemplateBlockVersionsPage',
);
const SchemaDefinitionDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/organization/components/cards/schema-definitions/SchemaDefinitionDetailPage'),
  'SchemaDefinitionDetailPage',
);
import { z } from 'zod';
import {
  CollectionThreadResolutionSchema,
  CollectionThreadSortBySchema,
  CollectionThreadStatusSchema,
  CollectionThreadTypeSchema,
} from '@erp/rpc';
const DebtorsReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/debitors/DebtorsReportPage'),
);
// Demo: the Reports nav shows the demo report-builder, not the real report grid.
// The real RebateReportsIndexPage file is intentionally left unrouted.
const RebateReportsIndexPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/demo-reports/RebateDemoReportsPage'),
);
const DivisionRebateSummaryPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/reports/DivisionRebateSummaryPage'),
);
const SupplierTagReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/SupplierTagReportPage'),
);
const DailyEarningsPYEPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/DailyEarningsPYEPage'),
);
const MonthlyTrendingPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/MonthlyTrendingPage'),
);
const SupplierPaymentTermsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/reports/SupplierPaymentTermsPage'),
);
const ProfitabilityReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/ProfitabilityReportPage'),
);
const SupplierYoYPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/SupplierYoYPage'),
);
const BudgetComparisonPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/reports/BudgetComparisonPage'),
);
const RebateDataExportPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/exports/RebateDataExportPage'),
);
const RebatesSettingsPage = lazyRouteComponent(
  () => import('./pages/authenticated/rebates/settings/RebatesSettingsPage'),
);
const VendorFollowupsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/vendor-followups/VendorFollowupsPage'),
);
import { ProductModule } from './hooks/organization-config/useOrganizationEnabledModules';
const QAPage = lazyRouteComponent(() => import('./pages/qa/QAPage'));
const AzureOauthFlowCallback = lazyRouteComponent(
  () =>
    import('./pages/authenticated/oauth-azure-callback/AzureOauthFlowCallback'),
);
import config from 'src/config';
import { buildPosthogHeaders } from 'src/api/posthogHeaders';

const InvoiceDemosPage = lazyRouteComponent(
  () => import('src/pages/accounts-payable/invoice-demos'),
  'InvoiceDemosPage',
);
const InternalUiDemoPage = lazyRouteComponent(
  () => import('src/pages/authenticated/internal-ui-demo/InternalUiDemoPage'),
  'InternalUiDemoPage',
);
const InvoicesPage = lazyRouteComponent(
  () => import('./pages/authenticated/accounts-payable/invoices/InvoicesPage'),
  'InvoicesPage',
);
const PaymentsPage = lazyRouteComponent(
  () => import('./pages/authenticated/accounts-payable/payments/PaymentsPage'),
);
import { Route as InvoiceDetailRoute } from './pages/authenticated/accounts-payable/invoices/$invoiceId';
const PurchaseOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-payable/purchase-orders/PurchaseOrdersPage'),
  'PurchaseOrdersPage',
);
import { Route as PurchaseOrderDetailRoute } from './pages/authenticated/accounts-payable/purchase-orders/$purchaseOrderId';
import { Route as PaymentDetailRoute } from './pages/authenticated/accounts-payable/payments/$paymentId';
const ARSummaryPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/summary/ARSummaryPage'),
);
// AR Vision Demo (GEN-2247) — demo-only "last 5 reminders" page (T7). Delete
// this lazy import, the `arDemoRemindersRoute` below, and its addChildren entry
// together with src/demo/ar.
const ARDemoReminderEmailsPage = lazyRouteComponent(
  () => import('./demo/ar/ReminderEmailsPage'),
);
// AR Technical Demo (F4 / GEN-2260) — demo-only bank-reconciliation view
// (scenes 12-14). Delete this lazy import, the `arDemoReconciliationRoute`
// below, and its addChildren entry together with src/demo/ar.
const ARDemoReconciliationPage = lazyRouteComponent(
  () => import('./demo/ar/ReconciliationPage'),
);
// AR Demo — blanket cash-receipt ingestion across all linked bank accounts.
// Delete this lazy import, the `arDemoCashReceiptIngestionRoute` below, and its
// addChildren entry together with src/demo/ar.
const ARDemoCashReceiptIngestionPage = lazyRouteComponent(
  () => import('./demo/ar/CashReceiptIngestionPage'),
);
// AR Email Intelligence Demo (GEN-2263) — demo-only Scene 0 corpus-learning
// page (E1). Delete this lazy import, the `arIntelCorpusRoute` below, and its
// addChildren entry together with src/demo/ar.
const ARIntelCorpusLearningPage = lazyRouteComponent(
  () => import('./demo/ar/email-intel/e1/CorpusLearningPage'),
  'CorpusLearningPage',
);
// AR Email Intelligence Demo (GEN-2264) — demo-only voice/draft hero surface
// (E2, Scenes 1–3). Delete this lazy import, the `arDemoVoiceIntelRoute` below,
// and its addChildren entry together with src/demo/ar.
const ARDemoVoiceIntelPage = lazyRouteComponent(
  () => import('./demo/ar/email-intel/voice/VoiceIntelPage'),
  'VoiceIntelPage',
);
// AR Email Intelligence Demo (E3 / GEN-2265) — demo-only Customer Intelligence
// panel (Scenes 4–5). Delete this lazy import, the `arDemoIntelligenceRoute`
// below, and its addChildren entry together with src/demo/ar/email-intel.
const ARDemoCustomerIntelligencePage = lazyRouteComponent(
  () => import('./demo/ar/email-intel/intelligence/CustomerIntelligencePage'),
);
// AR Email Intelligence Demo (GEN-2266) — E4 Scene 6 payoff page. Delete this
// lazy import, the `arDemoIntelSendRoute` below, and its addChildren entry
// together with frontend/src/demo/ar/email-intel.
const ARDemoIntelSendPayoffPage = lazyRouteComponent(
  () => import('./demo/ar/email-intel/payoff/IntelSendPayoffPage'),
);
const ARPaymentsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/payments/ARPaymentsPage'),
  'ARPaymentsPage',
);
const ARInvoicesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/invoices/ARInvoicesPage'),
);
import { ARInvoiceDetailRoute } from './pages/authenticated/accounts-receivable/invoices/$arInvoiceId';
import { ARPaymentDetailRoute } from './pages/authenticated/accounts-receivable/payments/$arPaymentId';
const RemittancesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/remittances/RemittancesPage'),
  'RemittancesPage',
);
import { RemittanceDetailRoute } from './pages/authenticated/accounts-receivable/remittances/$remittanceId';
const ARCashReceiptsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/cash-receipts/ARCashReceiptsPage'),
  'ARCashReceiptsPage',
);
const IngestedSalesOrdersPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/ingested-sales-orders/IngestedSalesOrdersPage'),
  'IngestedSalesOrdersPage',
);
import { CashReceiptDetailRoute } from './pages/authenticated/accounts-receivable/cash-receipts/$receiptId';
const ARReportsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/reports/ARReportsPage'),
);
const ARReportsListPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/reports/ARReportsListPage'),
);
const ARSavedReportPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/reports/saved/$savedReportId'),
  'ARSavedReportPage',
);
const ARCollectionsPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/collections/ARCollectionsPage'),
  'ARCollectionsPage',
);
const ARCustomerProfilePage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/collections/ARCustomerProfilePage'),
  'ARCustomerProfilePage',
);
const CollectionThreadDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/accounts-receivable/collections/CollectionThreadDetailPage'),
  'CollectionThreadDetailPage',
);
import { SavedReportNavItems } from './components/sidenav/saved-report-nav-items';
const CompaniesPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/CompaniesPage'),
);
const ContactsPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/ContactsPage'),
);
const CompanyDetailPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/CompanyDetailPage'),
);
const CompanyContactsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'ContactsTabPage',
);
const CompanyAddressesTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'AddressesTabPage',
);
const CompanyAliasesTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'AliasesTabPage',
);
const CompanyModulesTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'ModulesTabPage',
);
const CompanySupplierInfoTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'SupplierInfoTabPage',
);
const CompanyWarehouseConstraintsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'WarehouseConstraintsTabPage',
);
const CompanyCustomerInfoTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'CustomerInfoTabPage',
);
const CompanyMetadataTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'MetadataTabPage',
);
const CompanyRebateAgreementsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'RebateAgreementsTabPage',
);
const CompanyRebateProductGroupsTabPage = lazyRouteComponent(
  () => import('./pages/authenticated/companies/tabs/companyTabPages'),
  'RebateProductGroupsTabPage',
);
const CustomerProductMatchesPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/customer-product-matches/CustomerProductMatchesPage'),
  'CustomerProductMatchesPage',
);
const ProductMarginsPage = lazyRouteComponent(
  () => import('./pages/authenticated/product-margins/ProductMarginsPage'),
  'ProductMarginsPage',
);
const ProductCatalogPage = lazyRouteComponent(
  () => import('./pages/authenticated/product-catalog/ProductCatalogPage'),
);
const DiscountsPage = lazyRouteComponent(
  () => import('./pages/authenticated/discounts/DiscountsPage'),
  'DiscountsPage',
);
const SharedReportPage = lazyRouteComponent(
  () => import('./pages/authenticated/shared/SharedReportPage'),
);
const SharedLinksPage = lazyRouteComponent(
  () => import('./pages/authenticated/shared-links/SharedLinksPage'),
  'SharedLinksPage',
);

const AGENTS_ROUTE_MODULES: ProductModule[] = [
  ProductModule.ORDER_MANAGEMENT,
  ProductModule.REBATES,
  ProductModule.PROCUREMENT,
  ProductModule.SHIP_AND_DEBIT,
  ProductModule.AP_INVOICING,
  ProductModule.AR_INVOICING,
  ProductModule.FAMOUS_INTEGRATION,
  ProductModule.ORDERFUL_EDI,
  ProductModule.ORACLE_EBS_INTEGRATION,
];

// tanstack router routes
// Create individual routes

const welcomeRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/welcome',
  component: WelcomePage,
  loader: () => ({
    crumb: 'Welcome',
  }),
});

const tenantRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/tenant',
  component: TenantPage,
  loader: () => ({
    crumb: 'Tenant',
  }),
  beforeLoad: ({ context }) => {
    if (!context.auth.currentUser?.tenantUser?.isAdmin) {
      throw redirect({ to: '/' });
    }
  },
});

// TODO remove this demo route
const invoiceDemosRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/invoice-demos',
  component: InvoiceDemosPage,
  loader: () => ({
    crumb: 'Invoice Demos',
  }),
});

const internalUiDemoRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/internal-ui-demo',
  component: InternalUiDemoPage,
  loader: () => ({
    crumb: 'Internal UI demo',
  }),
});

const customersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/customers',
  staticData: {
    app: {
      nav: {
        label: 'Customers',
        icon: <BuildingsIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 10,
        groupOrder: 10,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.Customer },
          { action: Action.Read, subject: Subject.Company },
        ],
      },
    },
  },
  component: CustomersPage,
  loader: () => ({
    crumb: 'Customers',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
  validateSearch: z.object({
    customerId: z.string().optional(),
  }),
});

const salesOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders',
  staticData: {
    app: {
      nav: {
        label: 'Sales Orders',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 20,
        // Hidden from the nav — superseded by the "Sales Order" (v100) tab. The
        // route stays so existing links / deep links keep working.
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraft },
        ],
      },
    },
  },
  component: SalesOrderDraftsPage,
  loader: () => ({
    crumb: 'Sales Orders',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    await requireAnyPermission({
      context,
      permissions: [
        { action: Action.Read, subject: Subject.SalesOrderDraft },
        { action: Action.Read, subject: Subject.SalesOrder },
      ],
    });
  },
  validateSearch: z.object({
    salesOrderId: z.string().optional(),
  }),
});

const salesOrderRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders/$salesOrderId',
  component: SalesOrderPage,
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
});

const enhancedSalesOrderDraftPageRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders/draft/$salesOrderDraftId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraft },
        ],
      },
    },
  },
  component: EnhancedSalesOrderDraftPage,
  loader: () => ({
    crumb: 'Sales Order Draft',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
});

// OMS v3 — read-only browse over ar_draft_sales_orders, gated on the
// SalesOrderDraftV3 RBAC subject (Read to view). Clone of the Sales Orders
// list/detail wired to the salesOrdersV3.list/get API; the legacy
// /sales-orders pages are left untouched.
const salesOrdersV3Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders-v3',
  staticData: {
    app: {
      nav: {
        label: 'Sales Orders v3',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 21,
        // Hidden from the nav — superseded by the "Sales Order" (v100) tab. The
        // route stays so existing links / deep links keep working.
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrdersV3Page,
  // Persist pagination in the URL so a refresh / shared link returns to the
  // same page and page size.
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  }),
  loader: () => ({
    crumb: 'Sales Orders v3',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});

const salesOrderV3DetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders-v3/$draftId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrderV3DetailPage,
  // Shareable UI state for the review screen: active content tab, active source
  // rail tab, and whether the workflow pipeline strip is expanded.
  validateSearch: z.object({
    tab: z.enum(['details', 'lines', 'margin']).optional(),
    rail: z.enum(['activity', 'emails', 'document']).optional(),
    pipeline: z.coerce.boolean().optional(),
  }),
  loader: () => ({
    crumb: 'Sales Order v3',
    // The breadcrumb navigates back to the list, not the current detail page.
    crumbTo: '/sales-orders-v3',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});

// Sales Order v100 — AI-first OMS ("Nami") demo. Native port of the
// docs/oms-demo prototype; all dummy data. Gated like OMS v3 so it appears next
// to "Sales Orders v3" for the same audience. Demo branch only.
const salesOrdersV100Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-order',
  staticData: {
    app: {
      nav: {
        label: 'Sales Order',
        icon: <SparkleIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrdersV100Page,
  validateSearch: z.object({
    view: z.enum(['orders', 'reports']).optional(),
  }),
  loader: () => ({ crumb: 'Sales Order' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.ORDER_MANAGEMENT] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});

const salesOrderV100BuildRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-order/build',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: AdminCanvasPage,
  loader: () => ({ crumb: 'OMS — build', crumbTo: '/sales-order' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.ORDER_MANAGEMENT] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});

const salesOrderV100DetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-order/$orderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrderV100ReviewPage,
  validateSearch: z.object({
    tab: z.enum(['details', 'lines']).optional(),
    rail: z.enum(['activity', 'emails', 'document']).optional(),
  }),
  loader: () => ({ crumb: 'Sales Order', crumbTo: '/sales-order' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.ORDER_MANAGEMENT] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});

const suppliersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/suppliers',
  component: CompaniesPage,
  loader: () => ({
    crumb: 'Suppliers',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES, ProductModule.PROCUREMENT],
    });
  },
});

const supplierDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/suppliers/$id',
  component: CompanyDetailPage,
  loader: () => ({
    crumb: `Supplier`,
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [
        ProductModule.REBATES,
        ProductModule.PROCUREMENT,
        ProductModule.AP_INVOICING,
      ],
    });
  },
});

const companiesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/companies',
  staticData: {
    app: {
      nav: {
        label: 'Companies',
        icon: <BuildingsIcon className="h-4 w-4" weight="fill" />,
        group: 'Organization',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES, ProductModule.AP_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Company }],
      },
    },
  },
  component: CompaniesPage,
  loader: () => ({
    crumb: 'Companies',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES, ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Company,
    });
  },
});

const companyContactsListRoute = createRoute({
  getParentRoute: () => companiesRoute,
  path: 'contacts',
  component: ContactsPage,
  loader: () => ({
    crumb: 'Contacts',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES, ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Company,
    });
  },
});

const companyDetailRoute = createRoute({
  getParentRoute: () => companiesRoute,
  path: '$id',
  component: CompanyDetailPage,
  loader: async ({ params, context }) => {
    // Fetch company data for breadcrumb using queryClient for caching
    const { queryClient } = context;
    const queryKey = [`core/companies/${params.id}`];

    try {
      // Use queryClient.fetchQuery to leverage caching
      const response = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchResponse = await fetch(
            `${config.apiBaseUrl}/core/companies/${params.id}`,
            { credentials: 'include', headers: buildPosthogHeaders() },
          );

          if (!fetchResponse.ok) {
            throw new Error('Failed to fetch company');
          }

          const data = await fetchResponse.json();
          return data.data;
        },
      });

      return {
        crumb: response?.company?.name || 'Company',
      };
    } catch (error) {
      // Silently handle error - fallback to default
    }

    return {
      crumb: 'Company',
    };
  },
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [
        ProductModule.REBATES,
        ProductModule.PROCUREMENT,
        ProductModule.AP_INVOICING,
      ],
    });
  },
});

const rebateSupplierDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/suppliers/$id',
  component: () => <CompanyDetailPage module="rebates" />,
  loader: () => ({
    crumb: `Rebate Supplier`,
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

// Tab subroutes shared by companyDetailRoute, supplierDetailRoute, and
// rebateSupplierDetailRoute. Each parent renders CompanyDetailPage (layout
// with sidebar + Outlet); tab content comes from these child routes.
// Tab paths are spelled out explicitly so TypeScript can preserve each
// route's literal `path` and register it in the router's type tree.
type ContactsTo =
  | '/companies/$id/contacts'
  | '/suppliers/$id/contacts'
  | '/rebates/suppliers/$id/contacts';

const makeCompanyTabChildren = <P extends AnyRoute>(
  parent: P,
  contactsTo: ContactsTo,
) => {
  const redirectToContacts = (id: string) =>
    redirect({
      to: contactsTo,
      params: { id },
      replace: true,
    });

  const gate = async (
    context: RouterContext,
    id: string,
    perm: { action: Action; subject: Subject },
  ) => {
    if (!(await context.authzClient.can(perm.action, perm.subject))) {
      throw redirectToContacts(id);
    }
  };

  const indexRoute = createRoute({
    getParentRoute: () => parent,
    path: '/',
    beforeLoad: ({ params }) => {
      throw redirectToContacts((params as { id: string }).id);
    },
  });
  const contactsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'contacts',
    component: CompanyContactsTabPage,
  });
  const addressesRoute = createRoute({
    getParentRoute: () => parent,
    path: 'addresses',
    component: CompanyAddressesTabPage,
  });
  const aliasesRoute = createRoute({
    getParentRoute: () => parent,
    path: 'aliases',
    component: CompanyAliasesTabPage,
  });
  const modulesRoute = createRoute({
    getParentRoute: () => parent,
    path: 'modules',
    component: CompanyModulesTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Update,
        subject: Subject.Company,
      }),
  });
  const supplierInfoRoute = createRoute({
    getParentRoute: () => parent,
    path: 'supplier-info',
    component: CompanySupplierInfoTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.Supplier,
      }),
  });
  const warehouseConstraintsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'warehouse-constraints',
    component: CompanyWarehouseConstraintsTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.Inventory,
      }),
  });
  const customerInfoRoute = createRoute({
    getParentRoute: () => parent,
    path: 'customer-info',
    component: CompanyCustomerInfoTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.Customer,
      }),
  });
  const metadataRoute = createRoute({
    getParentRoute: () => parent,
    path: 'metadata',
    component: CompanyMetadataTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Update,
        subject: Subject.Company,
      }),
  });
  const rebateAgreementsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'rebate-agreements',
    component: CompanyRebateAgreementsTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.RebateAgreement,
      }),
  });
  const rebateProductGroupsRoute = createRoute({
    getParentRoute: () => parent,
    path: 'rebate-product-groups',
    component: CompanyRebateProductGroupsTabPage,
    beforeLoad: ({ context, params }) =>
      gate(context as RouterContext, (params as { id: string }).id, {
        action: Action.Read,
        subject: Subject.RebateAgreement,
      }),
  });

  return [
    indexRoute,
    contactsRoute,
    addressesRoute,
    aliasesRoute,
    modulesRoute,
    supplierInfoRoute,
    warehouseConstraintsRoute,
    customerInfoRoute,
    metadataRoute,
    rebateAgreementsRoute,
    rebateProductGroupsRoute,
  ] as const;
};

const companyDetailTabRoutes = makeCompanyTabChildren(
  companyDetailRoute,
  '/companies/$id/contacts',
);
const supplierDetailTabRoutes = makeCompanyTabChildren(
  supplierDetailRoute,
  '/suppliers/$id/contacts',
);
const rebateSupplierDetailTabRoutes = makeCompanyTabChildren(
  rebateSupplierDetailRoute,
  '/rebates/suppliers/$id/contacts',
);

const orderMetricsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/order-metrics',
  staticData: {
    app: {
      nav: {
        label: 'Order Metrics',
        icon: <PresentationChartIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 60,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        featureFlag: 'show-email-po-metrics-enabled',
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: OrderMetricsDashboard,
  loader: () => ({
    crumb: 'Order Metrics',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
  validateSearch: z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    groupBy: z.enum(['daily', 'weekly', 'monthly']).optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    sources: z.array(z.string()).optional(),
  }),
});

const salesReportsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-reports',
  staticData: {
    app: {
      nav: {
        label: 'Reports',
        icon: <ChartBarIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 65,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: SalesReportsPage,
  loader: () => ({ crumb: 'Reports' }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
  validateSearch: z.object({
    range: z.enum(['30d', '90d', 'all']).optional(),
  }),
});

const famousEDIRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/famous-edi',
  staticData: {
    app: {
      nav: {
        label: 'Famous EDI (Invoices)',
        icon: <ArrowCounterClockwiseIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 50,
      },
      access: {
        modulesAllOf: [
          ProductModule.ORDERFUL_EDI,
          ProductModule.FAMOUS_INTEGRATION,
        ],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: FamousEDIPage,
  loader: () => ({
    crumb: 'Famous EDI',
  }),
  validateSearch: z.object({
    invoiceId: z.string().uuid().optional(),
    // Filter state lives in the URL so links are shareable and reloads
    // preserve what the user was looking at. YYYY-MM-DD for dates.
    orderId: z.string().optional(),
    purchaseOrderNumber: z.string().optional(),
    customerId: z.string().uuid().optional(),
    ediInvoiceType: z.enum(['810', '880']).optional(),
    ediSendStatus: z.enum(['sent', 'error', 'not_sent']).optional(),
    importedFrom: z.string().optional(),
    importedTo: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      allOf: [ProductModule.ORDERFUL_EDI, ProductModule.FAMOUS_INTEGRATION],
    });
  },
});

const famousEDIShipmentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/famous-edi-shipments',
  staticData: {
    app: {
      nav: {
        label: 'Famous EDI (Shipments)',
        icon: <TruckIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 55,
      },
      access: {
        modulesAllOf: [
          ProductModule.ORDERFUL_EDI,
          ProductModule.FAMOUS_INTEGRATION,
        ],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: FamousEDIShipmentsPage,
  loader: () => ({
    crumb: 'Famous EDI Shipments',
  }),
  validateSearch: z.object({
    famousShipmentId: z.string().optional(),
    purchaseOrderNumber: z.string().optional(),
    customerId: z.string().uuid().optional(),
    edi856Status: z.enum(['not_sent', 'pending', 'sent', 'error']).optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      allOf: [ProductModule.ORDERFUL_EDI, ProductModule.FAMOUS_INTEGRATION],
    });
  },
});

const famousEDIShipmentDetailRoute = createRoute({
  getParentRoute: () => famousEDIShipmentsRoute,
  path: '/$shipmentId',
  component: FamousEDIShipmentDetailRoute,
});

const famousEDIOrderConfirmationRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/famous-edi-order-confirmation',
  staticData: {
    app: {
      nav: {
        label: 'Famous EDI (Order Confirmation)',
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 56,
      },
      access: {
        modulesAllOf: [
          ProductModule.ORDERFUL_EDI,
          ProductModule.FAMOUS_INTEGRATION,
        ],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: FamousEDIOrderConfirmationPage,
  loader: () => ({
    crumb: 'Famous EDI Order Confirmation',
  }),
  validateSearch: z.object({
    famousOrderId: z.string().optional(),
    customerId: z.string().uuid().optional(),
    edi855Status: z.enum(['not_sent', 'pending', 'sent', 'error']).optional(),
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      allOf: [ProductModule.ORDERFUL_EDI, ProductModule.FAMOUS_INTEGRATION],
    });
  },
});

const famousEDIOrderConfirmationDetailRoute = createRoute({
  getParentRoute: () => famousEDIOrderConfirmationRoute,
  path: '/$orderConfirmationId',
  component: OrderConfirmationDetailRoute,
});

const famousEdi855Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/famous-edi-855',
  staticData: {
    app: {
      nav: {
        label: '855 Acknowledgments',
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 58,
      },
      access: {
        modulesAllOf: [
          ProductModule.ORDERFUL_EDI,
          ProductModule.FAMOUS_INTEGRATION,
        ],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: FamousEdi855Page,
  loader: () => ({
    crumb: '855 Acknowledgments',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      allOf: [ProductModule.ORDERFUL_EDI, ProductModule.FAMOUS_INTEGRATION],
    });
  },
});

const ediTextMessagesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/edi-text-messages',
  staticData: {
    app: {
      nav: {
        label: 'EDI Text Messages',
        icon: <EnvelopeSimpleIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 57,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDERFUL_EDI],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: EdiTextMessagesPage,
  loader: () => ({
    crumb: 'EDI Text Messages',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDERFUL_EDI],
    });
  },
});

// const rebatesSubmittedRoute = createRoute({
//   getParentRoute: () => pathlessAuthenticatedRoute,
//   path: '/rebates/submitted',
//   component: SubmittedRebatesPage,
// });

const rebateDemoAnalyticsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/analytics-demo',
  staticData: {
    app: {
      nav: {
        label: 'Analytics (AI demo)',
        icon: <TrendUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 31,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateDemoAnalyticsPage,
  loader: () => ({
    crumb: 'Analytics (AI demo)',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesForecastingRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/analytics',
  staticData: {
    app: {
      nav: {
        label: 'Analytics',
        icon: <TrendUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebatesAnalyticsPage,
  loader: () => ({
    crumb: 'Analytics',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    cumulative: z.boolean().optional(),
    ruleTagIds: z.array(z.string()).optional(),
    ruleType: z.array(z.enum(['conditional', 'non-conditional'])).optional(),
    agreementTagIds: z.array(z.string()).optional(),
    supplierTagIds: z.array(z.string()).optional(),
    agreementIds: z.array(z.string()).optional(),
    supplierIds: z.array(z.string()).optional(),
    divisionIds: z.array(z.string()).optional(),
    year: z.number().optional(),
    visibleLines: z.array(z.string()).optional(),
    primaryLine: z.string().optional(),
  }),
});

const rebatesDebtorsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/debtors',
  staticData: {
    app: {
      nav: {
        label: 'Debtors Report',
        icon: <BankIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: DebtorsReportPage,
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  loader: () => ({
    crumb: 'Debtors Report',
  }),
  validateSearch: z.object({
    years: z.array(z.number()).optional(),
    showClosed: z.boolean().optional(),
    dataSources: z
      .array(z.enum(['estimates', 'reported', 'predictions']))
      .optional(),
    throughMonth: z.number().int().min(1).max(12).optional(),
  }),
});

const rebatesTierProximityRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/tier-proximity',
  staticData: {
    app: {
      nav: {
        label: 'Tier Proximity',
        icon: <TargetIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 50,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateTierProximityPage,
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  loader: () => ({
    crumb: 'Tier Proximity',
  }),
});

const rebatesAuditsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/audits',
  staticData: {
    app: {
      nav: {
        label: 'Audits',
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 60,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateAuditsPage,
  loader: () => ({
    crumb: 'Audits',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    year: z.number().optional(),
  }),
});

const rebatesAuditDetailRoute = createRoute({
  getParentRoute: () => rebatesAuditsRoute,
  path: '$agreementId',
  component: RebateAuditDetailPage,
  loader: () => ({
    crumb: 'Audit Detail',
  }),
  validateSearch: z.object({
    year: z.number().optional(),
  }),
});

// const rebatesReconciliationRoute = createRoute({
//   getParentRoute: () => pathlessAuthenticatedRoute,
//   path: '/rebates/reconciliation',
//   component: RebatesReconciliationPage,
// });

const rebatesAgreementsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/agreements',
  staticData: {
    app: {
      nav: {
        label: 'Agreements',
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateAgreementsPage,
  loader: () => ({
    crumb: 'Agreements',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    search: z.string().optional(),
    year: z.number().optional(),
    showClosed: z.boolean().optional(),
    filterDuplicates: z.boolean().optional(),
    filterFinalized: z.boolean().optional(),
    filterDraft: z.boolean().optional(),
    filterMultiyear: z.boolean().optional(),
    filterClosed: z.boolean().optional(),
    filterTags: z.array(z.string()).optional(),
  }),
});

// Layout route for agreement detail - handles shared loader and auth
const rebatesAgreementLayoutRoute = createRoute({
  getParentRoute: () => rebatesAgreementsRoute,
  path: '$id',
  beforeLoad: async ({ context, params }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });

    // Fetch agreement data and extend context for child routes
    const { queryClient } = context;

    try {
      const result = await queryClient.fetchQuery(
        orpc.rebates.agreements.getById.queryOptions({
          input: { rebateAgreementId: params.id },
        }),
      );

      return { agreement: result.data };
    } catch {
      return { agreement: null };
    }
  },
  loader: ({ context }) => {
    // Use agreement from beforeLoad context for breadcrumb
    const { agreement } = context as { agreement?: { name?: string } };
    return {
      crumb: agreement?.name || 'Agreement Detail',
    };
  },
  validateSearch: z.object({
    version: z.string().optional(),
  }),
});

// Index route - matches /rebates/agreements/{id} (no slug)
// Redirects to canonical URL with slug for better URLs
const rebatesAgreementIndexRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: '/',
  beforeLoad: ({ context, params, search }) => {
    // Get agreement from parent's beforeLoad context
    const { agreement } = context as { agreement?: { name?: string } };

    if (agreement?.name) {
      // Redirect to canonical URL with slug
      const slug = slugify(agreement.name);
      if (slug) {
        throw redirect({
          to: '/rebates/agreements/$id/$slug',
          params: { id: params.id, slug },
          search,
          replace: true,
        });
      }
    }
  },
  component: RebateAgreementDetailPage,
});

// Slug route - matches /rebates/agreements/{id}/{slug}
// Static paths like 'create-version' and 'audit' take priority over this dynamic route
const rebatesAgreementSlugRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: '$slug',
  component: RebateAgreementDetailPage,
});

const RebateAgreementCreateVersionPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/rebates/agreements/RebateAgreementCreateVersionPage'),
);
import { orpc } from './api/rpc/client';

const rebatesAgreementCreateVersionRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: 'create-version',
  component: RebateAgreementCreateVersionPage,
  loader: () => ({
    crumb: 'Create New Version',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebateCalculationAuditRoute = createRoute({
  getParentRoute: () => rebatesAgreementLayoutRoute,
  path: 'audit',
  component: RebateCalculationAuditPage,
  loader: () => ({
    crumb: 'Calculation Audit',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
  validateSearch: z.object({
    date: z.string().optional(),
    calculationType: z.enum(['estimate', 'prediction']).optional(),
  }),
});

const rebatesSupplierCollectionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/supplier-collections/$supplierId',
  component: SupplierCollectionsPage,
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesSupplierReportedAmountsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/supplier-reported-amounts/$supplierId',
  component: SupplierReportedAmountsPage,
  loader: () => ({
    crumb: 'Supplier Reported Amounts',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesUploadDataRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/upload-data',
  staticData: {
    app: {
      nav: {
        label: 'Upload Data',
        icon: <ArrowCounterClockwiseIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 10,
        groupOrder: 50,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Execute, subject: Subject.RebateDataUpload },
        ],
      },
    },
  },
  component: DataUploadRouter,
  loader: () => ({
    crumb: 'Upload Data',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataDemoResultsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/upload-data/demo-results/$uploadId',
  component: DemoResultsPage,
  loader: () => ({
    crumb: 'Processing Results',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesUploadDataEmailQueueRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/upload-data/email-queue',
  staticData: {
    app: {
      nav: {
        label: 'Email Queue',
        icon: <EnvelopeSimpleIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 11,
        groupOrder: 50,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Execute, subject: Subject.RebateDataUpload },
        ],
      },
    },
  },
  component: EmailUploadQueuePage,
  loader: () => ({
    crumb: 'Email Queue',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataAIRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'ai',
  component: ConvertDataPageWorkflow,
  validateSearch: z.object({
    sessionId: z.string().uuid().optional(),
  }),
  loader: () => ({
    crumb: 'AI-Powered Transform',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataManualRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'manual',
  component: ManualRebateCSVUpload,
  loader: () => ({
    crumb: 'Manual Upload',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataJobsRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'jobs',
  component: RebateCsvTransformJobsPage,
  loader: () => ({
    crumb: 'Jobs',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesUploadDataRunRoute = createRoute({
  getParentRoute: () => rebatesUploadDataRoute,
  path: 'run/$sessionId',
  component: RebateCsvTransformRunPage,
  loader: () => ({
    crumb: 'Run Timeline',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Execute,
      subject: Subject.RebateDataUpload,
    });
  },
});

const rebatesReportsIndexRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/reports',
  staticData: {
    app: {
      nav: {
        label: 'Reports',
        icon: <ChartBarIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 70,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateReportsIndexPage,
  loader: () => ({
    crumb: 'Reports',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesDivisionSummaryRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'division-summary',
  component: DivisionRebateSummaryPage,
  loader: () => ({
    crumb: 'Division Rebate Summary',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesSupplierTagSummaryRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'supplier-tag-summary',
  component: SupplierTagReportPage,
  loader: () => ({
    crumb: 'Supplier Tag Summary',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesDailyEarningsPYERoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'daily-earnings-pye',
  component: DailyEarningsPYEPage,
  loader: () => ({
    crumb: 'Daily Earnings PYE',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesMonthlyTrendingRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'monthly-trending',
  component: MonthlyTrendingPage,
  loader: () => ({
    crumb: 'Monthly Trending',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesSupplierPaymentTermsRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'supplier-payment-terms',
  component: SupplierPaymentTermsPage,
  loader: () => ({
    crumb: 'Supplier Payment Terms',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesProfitabilityReportRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'profitability',
  component: ProfitabilityReportPage,
  loader: () => ({
    crumb: 'Profitability Report',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
  validateSearch: z.object({
    year: z.number().optional(),
    month: z.number().nullable().optional(),
    lineType: z.enum(['estimates', 'predictions', 'reported']).optional(),
    dimensions: z.string().optional(),
    sortBy: z.string().optional(),
    showDollar: z.boolean().optional(),
  }),
});

const rebatesSupplierYoYRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'supplier-yoy',
  component: SupplierYoYPage,
  loader: () => ({
    crumb: 'Supplier YoY',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesBudgetComparisonRoute = createRoute({
  getParentRoute: () => rebatesReportsIndexRoute,
  path: 'budget-comparison',
  component: BudgetComparisonPage,
  loader: () => ({
    crumb: 'Budget vs. Forecast',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rebatesDataExportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/data-export',
  staticData: {
    app: {
      nav: {
        label: 'Data Export',
        icon: <DownloadIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 80,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: RebateDataExportPage,
  loader: () => ({
    crumb: 'Data Export',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
});

const rebatesSettingsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/settings',
  staticData: {
    app: {
      nav: {
        label: 'Settings',
        icon: <GearIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 100,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateConfig },
          { action: Action.Update, subject: Subject.RebateConfig },
        ],
      },
    },
  },
  component: RebatesSettingsPage,
  loader: () => ({
    crumb: 'Settings',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requireAnyPermission({
      context,
      permissions: [
        { action: Action.Read, subject: Subject.RebateConfig },
        { action: Action.Update, subject: Subject.RebateConfig },
      ],
    });
  },
});

const rebatesVendorFollowupsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rebates/vendor-followups',
  staticData: {
    app: {
      nav: {
        label: 'Vendor Followups',
        icon: <EnvelopeSimpleIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 65,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.RebateAgreement },
        ],
      },
    },
  },
  component: VendorFollowupsPage,
  loader: () => ({
    crumb: 'Vendor Followups',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.RebateAgreement,
    });
  },
  validateSearch: z.object({
    year: z.coerce.number().optional(),
  }),
});

const priceQuoteSheetDashboardRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/price-sheet-dashboard',
  staticData: {
    app: {
      nav: {
        label: 'Price Sheet Dashboard',
        icon: <TableIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 10,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetDashboard,
  loader: () => ({
    crumb: 'Price Sheet Dashboard',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ExternalPriceSheet,
      redirectTo: '/',
    });
  },
});

const priceQuoteSheetDashboardDetailRoute = createRoute({
  getParentRoute: () => priceQuoteSheetDashboardRoute,
  path: '/detail/$quoteSheetId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetDashboardDetailRoute,
});

const priceQuoteSheetInboxRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/price-sheet-inbox',
  staticData: {
    app: {
      nav: {
        label: 'Price Sheets',
        icon: <CurrencyDollarIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 20,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetInboxPage,
  loader: () => ({
    crumb: 'Price Sheet Inbox',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ExternalPriceSheet,
      redirectTo: '/',
    });
  },
});

const priceQuoteSheetInboxDetailRoute = createRoute({
  getParentRoute: () => priceQuoteSheetInboxRoute,
  path: '/$quoteSheetId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ExternalPriceSheet },
        ],
      },
    },
  },
  component: PriceQuoteSheetInboxDetailRoute,
});

// Layout route — provides the "Warehouses" breadcrumb and renders <Outlet />
// so the index (list) route or the detail route can take over the body.
const warehousesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inventory-management/warehouses',
  staticData: {
    app: {
      nav: {
        label: 'Warehouses',
        icon: <BuildingsIcon className="h-4 w-4" weight="fill" />,
        group: 'Inventory Management',
        order: 10,
        groupOrder: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Warehouse }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Warehouses',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.INVENTORY_MANAGEMENT],
    });
  },
});

const warehousesIndexRoute = createRoute({
  getParentRoute: () => warehousesRoute,
  path: '/',
  component: WarehousesPage,
});

const warehouseDetailRoute = createRoute({
  getParentRoute: () => warehousesRoute,
  path: '$warehouseId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Warehouse }],
      },
    },
  },
  component: WarehouseDetailRoute,
});

// Integration Marketplace Demo (?demo=im) — fake CDC ingest workflow builder.
// Not in the sidebar; reached only from the Data Sources settings flow (the
// AI "Create workflow" action and the link's created-workflow rows). Delete
// with frontend/src/demo/im.
const imWorkflowBuilder = lazyRouteComponent(
  () => import('./demo/im/workflow-builder/WorkflowBuilderPage'),
  'WorkflowBuilderPage',
);
const imWorkflowBuilderRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/im/workflow',
  component: imWorkflowBuilder,
  validateSearch: z.object({
    sourceId: z.string().catch(''),
    linkId: z.string().catch(''),
    workflowId: z.string().optional(),
  }),
  loader: () => ({ crumb: 'Workflow Builder' }),
});

const itemCatalogRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/catalog',
  staticData: {
    app: {
      nav: {
        label: 'Item Catalog',
        icon: <TableIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 10,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ItemCatalogPage,
  loader: () => ({
    crumb: 'Item Catalog',
  }),
});

const reviewsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/reviews',
  staticData: {
    app: {
      nav: {
        label: 'Reviews',
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 20,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ReviewsPage,
  loader: () => ({
    crumb: 'Reviews',
  }),
});

const reviewDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/reviews/$reviewId',
  staticData: {
    app: {
      access: {
        featureFlag: 'item-master-ui-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: ReviewDetailPage,
  loader: ({ params }) => ({
    crumb: params.reviewId,
  }),
});

const pricelistManagementRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/item-master/pricelist-management',
  staticData: {
    app: {
      nav: {
        label: 'PDF to CSV Extraction',
        icon: <TagIcon className="h-4 w-4" weight="fill" />,
        group: 'Item Master',
        order: 30,
        groupOrder: 27,
      },
      access: {
        featureFlag: 'im-pricelist-management',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Product }],
      },
    },
  },
  component: PricelistManagementPage,
  loader: () => ({
    crumb: 'PDF to CSV Extraction',
  }),
});

const replenishmentRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/replenishment',
  staticData: {
    app: {
      nav: {
        label: 'Replenishment',
        icon: <TrendUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 10,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
          { action: Action.Create, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: ReplenishmentPage,
  validateSearch: z.object({
    // Comma-separated list of division UUIDs for the multi-select filter.
    // Stored as a single string in the URL (e.g. ?divisionIds=uuid1,uuid2) so
    // shared links roundtrip cleanly without tanstack's array-encoding scheme.
    divisionIds: z.string().optional(),
    // Legacy single-division param. Still accepted for backward-compatible
    // links; the page normalizes both into a single array internally.
    divisionId: z.string().uuid().optional(),
    // Comma-separated destination-warehouse UUIDs, scoped to the selected
    // divisions (e.g. ?warehouseIds=uuid1,uuid2). Empty/absent = all warehouses.
    warehouseIds: z.string().optional(),
    // Buy-vs-transfer view toggle. 'all' (default) shows both supplier carts and
    // vendorless transfers; 'po' shows only supplier carts; 'to' shows only
    // transfers. Persisted to user prefs as well.
    view: z.enum(['all', 'po', 'to']).optional(),
    // When true, hide supplier cards with no open sales-order lines (deficit-only
    // suppliers) so the buyer can focus on must-act demand.
    soOnly: z.boolean().optional(),
    // When true, reveal trashed (rejected) suggestions across the board so the
    // buyer can review + restore them.
    showTrashed: z.boolean().optional(),
    // Focus mode: condense the page chrome (title, filters, belt, tabs) down to
    // a compact supplier strip + the selected cart detail.
    focus: z.boolean().optional(),
    supplierId: z.string().uuid().optional(),
    suggestionId: z.string().uuid().optional(),
    sort: z.string().optional(),
    // Comma-separated cart-view chip filters (e.g. ?chips=sos,below_mov).
    // Shared between AssignmentBoard's toolbar and SuggestionDetailModal's
    // prev/next sibling list so both honor the same filter.
    chips: z.string().optional(),
    // Cart-view free-text search across SKU / name / warehouse.
    q: z.string().optional(),
    // Selected ship-to (PO cart) within the flat supplier table. When set, the
    // AssignmentBoard table is scoped to that cart; absent = show all carts.
    shipTo: z.string().uuid().optional(),
    // When true, the synthetic "Vendorless transfers" bucket is selected
    // (mutually exclusive with supplierId) — drills into the transfers list.
    vendorless: z.boolean().optional(),
  }),
  loader: () => ({
    crumb: 'Replenishment',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const replenishmentSupplierDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/replenishment/suppliers/$supplierId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
          { action: Action.Create, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: SupplierDetailPage,
  loader: () => ({
    crumb: 'Supplier Detail',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

// Deprecated: the Replenishment Orders browser was folded into the PO Status
// and TO Status pages (which now have Origin=Replenishment + Division filters
// and Created-by / Lines columns). Kept as a redirect so old bookmarks/links —
// PO tab → PO Status, TO tab → TO Status — keep working.
const replenishmentOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/replenishment/orders',
  staticData: {
    app: {
      nav: {
        label: 'Replenishment Orders',
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 31,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: ReplenishmentOrdersPage,
  validateSearch: z.object({
    type: z.enum(['po', 'to']).optional(),
    search: z.string().optional(),
    status: z.array(z.string()).optional(),
    divisionIds: z.array(z.string().uuid()).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
  loader: () => ({
    crumb: 'Replenishment Orders',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const salesOrderStatusRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/sales-order-status',
  staticData: {
    app: {
      nav: {
        label: 'Sales Order Status',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 26,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: SalesOrderStatusPage,
  validateSearch: z.object({
    search: z.string().optional(),
    status: z.array(z.string()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
  loader: () => ({
    crumb: 'Sales Order Status',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const transferOrderStatusRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/transfer-order-status',
  staticData: {
    app: {
      nav: {
        label: 'Transfer Order Status',
        icon: <ArrowsLeftRightIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 27,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAllOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: TransferOrderStatusPage,
  validateSearch: z.object({
    search: z.string().optional(),
    status: z.array(z.string()).optional(),
    originSystems: z.array(z.string()).optional(),
    divisionIds: z.array(z.string().uuid()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
  loader: () => ({
    crumb: 'Transfer Order Status',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const as400RawDataRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/as400-raw-data',
  staticData: {
    app: {
      nav: {
        label: 'AS400 Raw Data',
        icon: <DatabaseIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 35,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        adminOnly: true,
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: AS400RawDataPage,
  loader: () => ({
    crumb: 'AS400 Raw Data',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const queryBuilderRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/query-builder',
  staticData: {
    app: {
      nav: {
        label: 'Query Builder',
        icon: <MagnifyingGlassIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 36,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: QueryBuilderPage,
  loader: () => ({
    crumb: 'Query Builder',
  }),
  // `q` holds a URL-safe JSON string encoding { base, mode, ir } for the
  // committed query. Kept permissive here; the page parses/guards the payload.
  validateSearch: z.object({ q: z.string().optional() }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const queryBuilderPrintRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/query-builder/print',
  component: QueryBuilderPrintPage,
  loader: () => ({ crumb: 'Report' }),
  validateSearch: z.object({ k: z.string().optional() }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.PROCUREMENT] });
  },
});

const obsoleteProductsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/obsolete-products',
  staticData: {
    app: {
      nav: {
        label: 'Obsolete & Returns',
        icon: <ArrowCounterClockwiseIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 37,
        groupOrder: 30,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ObsoleteProductsPage,
  loader: () => ({
    crumb: 'Obsolete & Returns',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

// DEMO: the old Replenishment addresses. The page moved to
// /procurement/replenishment (procDemoReplenishmentRoute); these keep old
// links and bookmarks working.
const demandRadarRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/demo',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const demandRadarIndexRoute = createRoute({
  getParentRoute: () => demandRadarRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/procurement/replenishment/board' });
  },
});

const demandRadarItemDetailRoute = createRoute({
  getParentRoute: () => demandRadarRoute,
  path: '$itemId',
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/procurement/replenishment/board/$itemId',
      params: { itemId: params.itemId },
    });
  },
});

// DEMO: Bausch + Lomb procurement suite routes (frontend/src/demo/procurement).
const procDemoOverviewRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/overview',
  staticData: {
    app: {
      nav: {
        label: 'Overview',
        icon: <ChartPieSliceIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 1,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementOverviewPage,
  loader: () => ({
    crumb: 'Overview',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const procDemoRequestsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/requests',
  staticData: {
    app: {
      nav: {
        label: 'Requests',
        icon: <TrayIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 2,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementRequestsPage,
  loader: () => ({
    crumb: 'Requests',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  validateSearch: z.object({
    id: z.string().optional(),
    // DEMO: list filter chip, kept in the address so Back restores it.
    filter: z.enum(['mine', 'needs-approval', 'direct', 'indirect']).optional(),
  }),
});

const procDemoSuppliersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/suppliers',
  staticData: {
    app: {
      nav: {
        label: 'Suppliers',
        icon: <BuildingsIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 3,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Suppliers',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const procDemoSuppliersIndexRoute = createRoute({
  getParentRoute: () => procDemoSuppliersRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementSuppliersPage,
  validateSearch: z.object({
    tab: z.enum(['suppliers', 'risk', 'materials', 'onboarding']).optional(),
    // DEMO: directory filters and sort, kept in the address so Back restores them.
    type: z.enum(['direct', 'indirect']).optional(),
    category: z.string().optional(),
    risk: z.enum(['critical-high', 'medium', 'low']).optional(),
    q: z.string().optional(),
    sort: z.enum(['spend', 'risk', 'otif']).optional(),
    dir: z.enum(['asc', 'desc']).optional(),
    // DEMO: the Risk tab. The open view, the open alert (a history step) and the
    // alert filters (replace) are in the address so Back and Forward follow them.
    view: z.enum(['alerts', 'exposure', 'tariffs', 'single-source']).optional(),
    alert: z.string().optional(),
    severity: z.string().optional(),
    dimension: z.string().optional(),
    status: z.string().optional(),
  }),
});

const procDemoSupplierDetailRoute = createRoute({
  getParentRoute: () => procDemoSuppliersRoute,
  path: '$supplierId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementSupplierDetailPage,
  loader: () => ({
    crumb: 'Supplier',
  }),
  // DEMO: the open tab is part of the address so Back returns to the previous tab.
  validateSearch: z.object({
    tab: z
      .enum(['overview', 'performance', 'agreements', 'risk', 'profile'])
      .optional(),
    // DEMO: the alert open in the supplier's Risk tab (a history step).
    alert: z.string().optional(),
  }),
});

const procDemoOnboardingCaseRoute = createRoute({
  getParentRoute: () => procDemoSuppliersRoute,
  path: 'onboarding/$caseId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementOnboardingCasePage,
  loader: () => ({
    crumb: 'Onboarding',
  }),
  // DEMO: the open step is part of the address so Back returns to the previous step.
  validateSearch: z.object({
    step: z
      .enum([
        'profile',
        'tax-banking',
        'financial',
        'legal',
        'quality',
        'insurance-esg',
        'agreements',
        'approvals',
      ])
      .optional(),
  }),
});

const procDemoMaterialRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/materials/$materialId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementMaterialComparePage,
  loader: () => ({
    crumb: 'Supplier comparison',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const procDemoSourcingRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/sourcing',
  staticData: {
    app: {
      nav: {
        label: 'Sourcing',
        icon: <GavelIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 4,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Sourcing',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const procDemoSourcingIndexRoute = createRoute({
  getParentRoute: () => procDemoSourcingRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementSourcingPage,
  // DEMO: list filter, kept in the address so Back from an event restores it.
  validateSearch: z.object({
    filter: z.enum(['open', 'awarded']).optional(),
  }),
});

const procDemoSourcingEventRoute = createRoute({
  getParentRoute: () => procDemoSourcingRoute,
  path: '$eventId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementSourcingEventPage,
  loader: () => ({
    crumb: 'Bid details',
  }),
  // DEMO: the open tab is part of the address so Back returns to the previous tab.
  validateSearch: z.object({
    tab: z
      .enum([
        'leveling',
        'scoring',
        'scenarios',
        'documents',
        'ask',
        'negotiation',
      ])
      .optional(),
    // DEMO: the bid document (and section) to show on the documents tab, as
    // `<document id>` or `<document id>:<section>`.
    source: z.string().optional(),
  }),
});

const procDemoContractsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/contracts',
  staticData: {
    app: {
      nav: {
        label: 'Contracts',
        icon: <SignatureIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 5,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Contracts',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

const procDemoContractsIndexRoute = createRoute({
  getParentRoute: () => procDemoContractsRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementContractsPage,
  validateSearch: z.object({
    q: z.string().optional(),
    // DEMO: the open tab is part of the address so Back returns to the previous tab.
    tab: z.enum(['agreements', 'renewals', 'clauses']).optional(),
  }),
});

const procDemoContractDetailRoute = createRoute({
  getParentRoute: () => procDemoContractsRoute,
  path: '$contractId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: ProcurementContractDetailPage,
  loader: () => ({
    crumb: 'Contract',
  }),
  validateSearch: z.object({
    clause: z.string().optional(),
    // DEMO: the open right-hand tab is part of the address. `agent` and `ask`
    // are old names of a tab that moved into the AI assistant; the page opens
    // Clauses for them.
    tab: z
      .enum(['clauses', 'agent', 'ask', 'obligations', 'compliance', 'related'])
      .optional(),
    // DEMO: the letter the AI assistant drafted, open in a dialog on the page.
    draft: z.enum(['notice_deadline', 'auto_renews', 'expires']).optional(),
  }),
});

const procDemoForecastSummaryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/forecast/summary',
  staticData: {
    app: {
      nav: {
        label: 'Forecast summary',
        icon: <ChartLineUpIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 6,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementForecastSummaryPage,
  loader: () => ({
    crumb: 'Forecast summary',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  // DEMO: filters, horizon, measure and sort, kept in the address so Back from
  // a series restores the same view of the grid.
  validateSearch: z.object({
    // 'distribution' (bought to resell) or 'manufacturing' (the panel shop).
    business: z.string().optional(),
    site: z.string().optional(),
    category: z.string().optional(),
    method: z.string().optional(),
    demandClass: z.string().optional(),
    approval: z.string().optional(),
    q: z.string().optional(),
    horizon: z.union([z.literal(6), z.literal(12)]).optional(),
    measure: z.enum(['quantity', 'cost']).optional(),
    sort: z.enum(['series', 'total']).optional(),
    dir: z.enum(['asc', 'desc']).optional(),
  }),
});

const procDemoForecastDetailsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/forecast/details',
  staticData: {
    app: {
      nav: {
        label: 'Forecast details',
        icon: <TableIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 7,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementForecastDetailsPage,
  loader: () => ({
    crumb: 'Forecast details',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  validateSearch: z.object({
    series: z.string().optional(),
    // DEMO: "What we buy" (parts) or "What we sell" (panels and kits); absent is the item's own forecast.
    view: z.enum(['buy', 'sell']).optional(),
  }),
});

// DEMO: the panel shop parts plan is the Manufacturing planning drill-down of
// the Replenishment page (and of the forecast pages), so it has no nav entry.
const procDemoBomForecastRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/forecast/bom',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementBomForecastPage,
  loader: () => ({
    crumb: 'Parts plan',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
});

// DEMO: the Replenishment page, one page for both of Pillar's businesses
// (the buyer board and the old replenishment list, consolidated). It lives
// under /board because the product's own replenishmentRoute above owns
// /procurement/replenishment (hidden in the nav) and its typed links depend on
// that path and search schema.
const procDemoReplenishmentRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/replenishment/board',
  staticData: {
    app: {
      nav: {
        label: 'Replenishment',
        icon: <KanbanIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 9,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Replenishment',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  // DEMO: the business view and the open cart ('none' when closed), so Back
  // returns to the previous cart.
  validateSearch: z.object({
    view: z.enum(['all', 'distribution', 'manufacturing']).optional(),
    cart: z.string().optional(),
  }),
});

const procDemoReplenishmentIndexRoute = createRoute({
  getParentRoute: () => procDemoReplenishmentRoute,
  path: '/',
  component: ProcurementReplenishmentPage,
});

const procDemoReplenishmentItemRoute = createRoute({
  getParentRoute: () => procDemoReplenishmentRoute,
  path: '$itemId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
      },
    },
  },
  component: DemandRadarItemDetailPage,
  loader: () => ({
    crumb: 'Item',
  }),
});

// DEMO: the old Buyer board address; the page is now Replenishment above.
const procDemoBuyerBoardRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/buyer-board',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  validateSearch: z.object({
    cart: z.string().optional(),
  }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/procurement/replenishment/board',
      search: { cart: search.cart },
    });
  },
});

const procDemoInvoiceComplianceRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/invoice-compliance',
  staticData: {
    app: {
      nav: {
        label: 'Invoice compliance',
        icon: <ShieldCheckIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 12,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementInvoiceCompliancePage,
  loader: () => ({
    crumb: 'Invoice compliance',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  validateSearch: z.object({
    invoice: z.string().optional(),
  }),
});

// DEMO: Supplier risk now lives in Suppliers (the Risk tab). This route only
// forwards old links and bookmarks there, keeping the open alert, view and filters.
const procDemoRiskRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/risk',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  beforeLoad: ({ context, search }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
    throw redirect({
      to: '/procurement/suppliers',
      search: {
        tab: 'risk',
        view: search.tab,
        alert: search.alert,
        severity: search.severity,
        dimension: search.dimension,
        status: search.status,
      },
      replace: true,
    });
  },
  validateSearch: z.object({
    alert: z.string().optional(),
    tab: z.enum(['alerts', 'exposure', 'tariffs', 'single-source']).optional(),
    severity: z.string().optional(),
    dimension: z.string().optional(),
    status: z.string().optional(),
  }),
});

const procDemoAgentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/agents',
  staticData: {
    app: {
      nav: {
        label: 'AI agents',
        icon: <SparkleIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 14,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProcurementAgentsPage,
  loader: () => ({
    crumb: 'AI agents',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  validateSearch: z.object({
    agent: z.string().optional(),
    view: z.string().optional(),
  }),
});

const procurementPurchaseOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/procurement/purchase-orders',
  staticData: {
    app: {
      nav: {
        label: 'Purchase Order Status',
        icon: <ShoppingCartIcon className="h-4 w-4" weight="fill" />,
        group: 'Procurement',
        order: 11,
        groupOrder: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  // DEMO: real Purchase Order Status page hidden behind the canned demo page.
  component: DemoPurchaseOrderStatusPage,
  loader: () => ({
    crumb: 'Purchase Order Status',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.PROCUREMENT],
    });
  },
  validateSearch: z.object({
    externalId: z.string().optional(),
    // DEMO: the open purchase order (Back closes it, Forward reopens it).
    po: z.string().optional(),
    supplierId: z.string().optional(),
    status: z.array(z.string()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    deliveryDateFrom: z.string().optional(),
    deliveryDateTo: z.string().optional(),
    originSystems: z.array(z.string()).optional(),
    divisionIds: z.array(z.string().uuid()).optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const procurementPurchaseOrderDetailRoute = createRoute({
  getParentRoute: () => procurementPurchaseOrdersRoute,
  path: '/$purchaseOrderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.PROCUREMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: ProcurementPurchaseOrderDetailRoute,
  loader: () => ({
    crumb: 'Purchase Order',
  }),
});

const inventoryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inventory-management/inventory',
  staticData: {
    app: {
      nav: {
        label: 'Inventory',
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Inventory Management',
        order: 20,
        groupOrder: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Inventory',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.INVENTORY_MANAGEMENT],
    });
  },
});

const inventoryIndexRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: '/',
  component: InventoryPage,
});

const inventoryProductDetailRoute = createRoute({
  getParentRoute: () => inventoryRoute,
  path: '$productId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProductDetailPage,
  validateSearch: z.object({
    // 'overall' (default) = all-warehouse view; 'warehouse' = single-warehouse
    // focus view scoped to `warehouseId`.
    view: z.enum(['overall', 'warehouse']).optional(),
    warehouseId: z.string().optional(),
    // Overall view: comma-joined warehouse ids scoping every data section.
    // Empty/absent = org-wide.
    warehouseIds: z.string().optional(),
  }),
  loader: async ({ params, context }) => {
    const { queryClient } = context;
    try {
      const result = await queryClient.fetchQuery(
        orpc.procurement.inventorySnapshots.getByProduct.queryOptions({
          input: { productId: params.productId },
        }),
      );
      return { crumb: result.productSku ?? result.productName ?? 'Product' };
    } catch {
      return { crumb: 'Product' };
    }
  },
});

const supplierProductsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inventory-management/supplier-products',
  staticData: {
    app: {
      nav: {
        label: 'Supplier Products',
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Inventory Management',
        order: 25,
        groupOrder: 25,
        hidden: true,
      },
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: () => <Outlet />,
  loader: () => ({
    crumb: 'Supplier Products',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.INVENTORY_MANAGEMENT],
    });
  },
});

const supplierProductsIndexRoute = createRoute({
  getParentRoute: () => supplierProductsRoute,
  path: '/',
  component: SupplierProductsPage,
});

const supplierProductDetailRoute = createRoute({
  getParentRoute: () => supplierProductsRoute,
  path: '$productId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.INVENTORY_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Inventory }],
      },
    },
  },
  component: ProductDetailPage,
  validateSearch: z.object({
    // 'overall' (default) = all-warehouse view; 'warehouse' = single-warehouse
    // focus view scoped to `warehouseId`.
    view: z.enum(['overall', 'warehouse']).optional(),
    warehouseId: z.string().optional(),
    // Overall view: comma-joined warehouse ids scoping every data section.
    // Empty/absent = org-wide.
    warehouseIds: z.string().optional(),
  }),
  loader: async ({ params, context }) => {
    const { queryClient } = context;
    try {
      const result = await queryClient.fetchQuery(
        orpc.procurement.supplierProducts.getByProduct.queryOptions({
          input: { productId: params.productId },
        }),
      );
      return { crumb: result.productSku ?? result.productName ?? 'Product' };
    } catch {
      return { crumb: 'Product' };
    }
  },
});

const apInvoicesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/invoices',
  staticData: {
    app: {
      nav: {
        label: 'Invoices',
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Payable',
        order: 10,
        groupOrder: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.APInvoice }],
      },
    },
  },
  component: InvoicesPage,
  loader: () => ({
    crumb: 'Invoices',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AP_INVOICING],
    });
  },
  validateSearch: z.object({
    invoiceNumber: z.string().optional(),
    supplierId: z.string().optional(),
    status: z.array(z.string()).optional(),
    paymentStatus: z.array(z.string()).optional(),
    invoiceDateFrom: z.string().optional(),
    invoiceDateTo: z.string().optional(),
    dueDateFrom: z.string().optional(),
    dueDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const apInvoiceDetailRoute = createRoute({
  getParentRoute: () => apInvoicesRoute,
  path: '/$invoiceId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.APInvoice }],
      },
    },
  },
  component: InvoiceDetailRoute,
});

const apPurchaseOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/purchase-orders',
  staticData: {
    app: {
      nav: {
        label: 'Purchase Orders',
        icon: <ShoppingCartIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Payable',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: PurchaseOrdersPage,
  loader: () => ({
    crumb: 'Purchase Orders',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AP_INVOICING],
    });
  },
  validateSearch: z.object({
    externalId: z.string().optional(),
    supplierId: z.string().optional(),
    status: z.array(z.string()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    deliveryDateFrom: z.string().optional(),
    deliveryDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const apPaymentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-payable/payments',
  staticData: {
    app: {
      nav: {
        label: 'Payments & Transactions',
        icon: <BankIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Payable',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.APInvoicePayment },
        ],
      },
    },
  },
  component: PaymentsPage,
  loader: () => ({
    crumb: 'Payments & Transactions',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AP_INVOICING],
    });
  },
  validateSearch: z.object({
    tab: z.enum(['payments', 'transactions']).optional(),
    status: z.string().optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    approvalRequest: z.string().uuid().optional(),
  }),
});

const apPurchaseOrderDetailRoute = createRoute({
  getParentRoute: () => apPurchaseOrdersRoute,
  path: '/$purchaseOrderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.PurchaseOrder },
        ],
      },
    },
  },
  component: PurchaseOrderDetailRoute,
});

const apPaymentDetailRoute = createRoute({
  getParentRoute: () => apPaymentsRoute,
  path: '/$paymentId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.APInvoicePayment },
        ],
      },
    },
  },
  component: PaymentDetailRoute,
});

// Accounts Receivable routes
const arSummaryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/summary',
  staticData: {
    app: {
      nav: {
        label: 'Summary',
        icon: <PresentationChartIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 10,
        groupOrder: 45,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARSummaryPage,
  loader: () => ({ crumb: 'Summary' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

// AR Vision Demo (GEN-2247) — demo-only Scene 6 route. No module/permission
// gate (UI-only demo). Delete with src/demo/ar.
const arDemoRemindersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/reminders',
  component: ARDemoReminderEmailsPage,
  loader: () => ({ crumb: 'Sent reminders' }),
});

// AR Technical Demo (F4 / GEN-2260) — demo-only Flow-3 reconciliation route. No
// module/permission gate (UI-only demo). Delete with src/demo/ar.
const arDemoReconciliationRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/reconciliation',
  component: ARDemoReconciliationPage,
  loader: () => ({ crumb: 'Bank reconciliation' }),
});

// AR Demo — blanket cash-receipt ingestion route (all linked bank accounts). No
// module/permission gate (UI-only demo). Delete with src/demo/ar.
const arDemoCashReceiptIngestionRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/cash-receipts',
  component: ARDemoCashReceiptIngestionPage,
  loader: () => ({ crumb: 'Cash receipts' }),
});

// AR Email Intelligence Demo (GEN-2263) — demo-only Scene 0 route. No
// module/permission gate (UI-only demo). Delete with src/demo/ar.
const arIntelCorpusRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/intel/corpus',
  component: ARIntelCorpusLearningPage,
  loader: () => ({ crumb: 'What I learned from your inbox' }),
});

// AR Email Intelligence Demo (GEN-2264) — demo-only voice/draft hero route
// (Scenes 1–3). No module/permission gate (UI-only demo). Delete with
// src/demo/ar.
const arDemoVoiceIntelRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/voice',
  component: ARDemoVoiceIntelPage,
  loader: () => ({ crumb: 'Voice intelligence' }),
});

// AR Email Intelligence Demo (E3 / GEN-2265) — demo-only Customer Intelligence
// panel route (Scenes 4–5). No module/permission gate (UI-only demo). Delete
// with src/demo/ar/email-intel.
const arDemoIntelligenceRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/intelligence',
  component: ARDemoCustomerIntelligencePage,
  loader: () => ({ crumb: 'Customer Intelligence' }),
});

// AR Email Intelligence Demo (GEN-2266) — demo-only Scene 6 payoff route. No
// module/permission gate (UI-only demo). Delete with frontend/src/demo/ar/email-intel.
const arDemoIntelSendRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/demo/ar/intel/send',
  component: ARDemoIntelSendPayoffPage,
  loader: () => ({ crumb: 'In your voice' }),
});

const arIngestedSalesOrdersRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/ingested-sales-orders',
  staticData: {
    app: {
      nav: {
        label: 'Ingested Sales Orders',
        icon: <ShoppingCartIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 15,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: IngestedSalesOrdersPage,
  loader: () => ({ crumb: 'Ingested Sales Orders' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requireAnyPermission({
      context,
      permissions: [{ action: Action.Read, subject: Subject.SalesOrder }],
    });
  },
  validateSearch: z.object({
    customerId: z.string().optional(),
    status: z.array(z.string()).optional(),
    orderDateFrom: z.string().optional(),
    orderDateTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arIngestedSalesOrderDetailRoute = createRoute({
  getParentRoute: () => arIngestedSalesOrdersRoute,
  path: '/$salesOrderId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: SalesOrderPage,
});

const arPaymentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/payments',
  staticData: {
    app: {
      nav: {
        label: 'Payments',
        icon: <BankIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARPayment }],
      },
    },
  },
  component: ARPaymentsPage,
  loader: () => ({ crumb: 'Payments' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARPayment,
    });
  },
  validateSearch: z.object({
    customerId: z.array(z.string()).optional(),
    status: z.array(z.string()).optional(),
    paymentMethod: z.array(z.string()).optional(),
    paymentDateFrom: z.string().optional(),
    paymentDateTo: z.string().optional(),
    search: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arRemittancesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/remittances',
  staticData: {
    app: {
      nav: {
        label: 'Remittances',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARPayment }],
      },
    },
  },
  component: RemittancesPage,
  loader: () => ({ crumb: 'Remittances' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARPayment,
    });
  },
  validateSearch: z.object({
    reviewFilter: z.enum(['needs_review', 'complete', 'all']).optional(),
    search: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arInvoicesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/invoices',
  staticData: {
    app: {
      nav: {
        label: 'Invoices',
        icon: <FileTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARInvoicesPage,
  loader: () => ({ crumb: 'Invoices' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
  validateSearch: z.object({
    customerId: z.array(z.string()).optional(),
    status: z.array(z.string()).optional(),
    invoiceDateFrom: z.string().optional(),
    invoiceDateTo: z.string().optional(),
    dueDateFrom: z.string().optional(),
    dueDateTo: z.string().optional(),
    firstSentAtFrom: z.string().optional(),
    firstSentAtTo: z.string().optional(),
    sentState: z.enum(['sent', 'unsent']).optional(),
    overdue: z.boolean().optional(),
    search: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arReportsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/reports',
  staticData: {
    app: {
      nav: {
        label: 'Reports',
        icon: <ChartBarIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 40,
        subItems: <SavedReportNavItems />,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARReportsListPage,
  loader: () => ({ crumb: 'Reports' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arNewReportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/reports/new',
  component: ARReportsPage,
  loader: () => ({ crumb: 'New Report' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arSavedReportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/reports/saved/$savedReportId',
  component: ARSavedReportPage,
  validateSearch: z.object({}),
  loader: () => ({
    crumb: 'Saved Report',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.AR_INVOICING],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arCollectionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/collections',
  staticData: {
    app: {
      nav: {
        label: 'Collections',
        icon: <UsersIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 35,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARInvoice }],
      },
    },
  },
  component: ARCollectionsPage,
  loader: () => ({ crumb: 'Collections' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
  validateSearch: z.object({
    search: z.string().optional(),
    tab: z.enum(['customers', 'threads']).optional(),
    // Threads tab view: inbox (grouped-by-customer, default) or list.
    view: z.enum(['inbox', 'list']).optional(),
    // Inbox selection (only meaningful when tab=threads & view=inbox).
    customerId: z.string().uuid().optional(),
    threadId: z.string().uuid().optional(),
    // Threads "list" view table state — server-side sort, filter, pagination.
    // Namespaced (thread*) so the filters don't collide with the inbox params.
    sortBy: CollectionThreadSortBySchema.optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().int().min(1).max(200).optional(),
    threadStatus: z.array(CollectionThreadStatusSchema).optional(),
    threadType: z.array(CollectionThreadTypeSchema).optional(),
    threadResolution: z.array(CollectionThreadResolutionSchema).optional(),
    threadSearch: z.string().optional(),
    // Restrict the list to threads assigned to the current user.
    threadAssignedToMe: z.boolean().optional(),
  }),
});

const arCustomerProfileRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/collections/$customerId',
  component: ARCustomerProfilePage,
  loader: () => ({ crumb: 'Customer Profile' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
});

const arCollectionThreadDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/collections/$customerId/threads/$threadId',
  component: CollectionThreadDetailPage,
  loader: () => ({ crumb: 'Thread' }),
  beforeLoad: async ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ARInvoice,
    });
  },
  // `from` marks which ranked list the user arrived from (AR summary card or
  // the Collections threads tab), enabling an origin-aware "Back" link and
  // Previous/Next walking of that list.
  validateSearch: z.object({
    from: z.enum(['summary', 'threads']).optional(),
  }),
});

const arInvoiceDetailRoute = createRoute({
  getParentRoute: () => arInvoicesRoute,
  path: '/$arInvoiceId',
  component: ARInvoiceDetailRoute,
});

const arPaymentDetailRoute = createRoute({
  getParentRoute: () => arPaymentsRoute,
  path: '/$arPaymentId',
  component: ARPaymentDetailRoute,
});

const arRemittanceDetailRoute = createRoute({
  getParentRoute: () => arRemittancesRoute,
  path: '/$remittanceId',
  component: RemittanceDetailRoute,
});

const arCashReceiptsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/accounts-receivable/cash-receipts',
  staticData: {
    app: {
      nav: {
        label: 'Cash Receipts',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Accounts Receivable',
        order: 15,
      },
      access: {
        modulesAnyOf: [ProductModule.AR_INVOICING],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.ARPayment }],
      },
    },
  },
  component: ARCashReceiptsPage,
  loader: () => ({ crumb: 'Cash Receipts' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.AR_INVOICING] });
  },
  validateSearch: z.object({
    statusTab: z.enum(['all', 'needs_review', 'matched']).optional(),
    paymentDateFrom: z.string().optional(),
    paymentDateTo: z.string().optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const arCashReceiptDetailRoute = createRoute({
  getParentRoute: () => arCashReceiptsRoute,
  path: '/$receiptId',
  component: CashReceiptDetailRoute,
});

const divisionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/divisions',
  staticData: {
    app: {
      nav: {
        label: 'Divisions',
        icon: <BuildingIcon className="h-4 w-4" weight="fill" />,
        group: 'Organization',
        order: 10,
        groupOrder: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.REBATES],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Division }],
      },
    },
  },
  component: DivisionsPage,
  loader: () => ({
    crumb: 'Divisions',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Division,
    });
  },
});

const divisionRoute = createRoute({
  getParentRoute: () => divisionsRoute,
  path: '$divisionId',
  component: DivisionDetailPage,
  loader: async ({ params, context }) => {
    // Fetch division data for breadcrumb using queryClient for caching
    const { queryClient } = context;
    const queryKey = [`organization/divisions/${params.divisionId}`];

    try {
      // Use queryClient.fetchQuery to leverage caching
      const response = await queryClient.fetchQuery({
        queryKey,
        queryFn: async () => {
          const fetchResponse = await fetch(
            `${config.apiBaseUrl}/organization/divisions/${params.divisionId}`,
            { credentials: 'include', headers: buildPosthogHeaders() },
          );

          if (!fetchResponse.ok) {
            throw new Error('Failed to fetch division');
          }

          const data = await fetchResponse.json();
          return data.data;
        },
      });

      return {
        crumb: response?.division?.name || 'Division Details',
      };
    } catch (error) {
      // Silently handle error - fallback to default
    }

    return {
      crumb: 'Division Details',
    };
  },
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const inboundEmailsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inbound-emails',
  component: InboundEmailsPage,
  loader: () => ({
    crumb: 'Inbound Emails',
  }),
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    inboundEmailId: z.string().optional(),
    search: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    cc: z.string().optional(),
    upn: z.string().optional(),
    sortBy: z
      .enum([
        'createdAt',
        'updatedAt',
        'from',
        'to',
        'subject',
        'receivedByMailbox',
        'messageId',
      ])
      .optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
  }),
});

const inboundEmailRulesRoute = createRoute({
  getParentRoute: () => inboundEmailsRoute,
  path: '/email-rules',
  component: InboundEmailRulesPage,
  loader: () => ({
    crumb: 'Email Rules',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context, redirectTo: '/inbound-emails' });
  },
});

const inboxRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/inbox',
  staticData: {
    app: {
      nav: {
        label: 'Inbox',
        icon: <TrayIcon className="h-4 w-4" weight="fill" />,
        group: 'Overview',
        order: 5,
        groupOrder: 1,
        pinned: true,
      },
      access: {},
    },
  },
  component: InboxPage,
  errorComponent: InboxErrorComponent,
  loader: () => ({
    crumb: 'Inbox',
  }),
  validateSearch: inboxSearchSchema,
});

const documentsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/documents',
  staticData: {
    app: {
      access: {},
    },
  },
  component: DocumentsPage,
  loader: () => ({
    crumb: 'Documents',
  }),
});

const fileExtractionRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/tools/file-extraction',
  staticData: {
    app: {
      access: {},
    },
  },
  component: FileExtractionPage,
  loader: () => ({
    crumb: 'File Extraction',
  }),
});

const documentRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/documents/$documentId',
  staticData: {
    app: {
      access: {},
    },
  },
  component: DocumentDetailPage,
});

const settingsOrganizationSearchSchema = z.object({
  section: z.string().optional(),
});

const settingsOrganizationLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/organization',
  component: SettingsOrganizationLayoutPage,
  validateSearch: settingsOrganizationSearchSchema,
  loader: () => ({
    crumb: 'Organization Settings',
  }),
});

const KNOWN_SECTION_IDS = new Set<OrganizationSettingsSectionId>(
  ORGANIZATION_SETTINGS_SECTIONS.map((s) => s.id),
);

const resolveSectionId = (
  requested: string | undefined,
): OrganizationSettingsSectionId => {
  if (!requested) return DEFAULT_ORG_SECTION_ID;
  if (LEGACY_SECTION_SLUG_MAP[requested])
    return LEGACY_SECTION_SLUG_MAP[requested];
  if (KNOWN_SECTION_IDS.has(requested as OrganizationSettingsSectionId))
    return requested as OrganizationSettingsSectionId;
  return DEFAULT_ORG_SECTION_ID;
};

const settingsOrganizationIndexRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: '/',
  validateSearch: settingsOrganizationSearchSchema,
  beforeLoad: ({ search }) => {
    throw redirect({ to: sectionPath(resolveSectionId(search.section)) });
  },
});

const settingsOrganizationSectionRoutes = ORGANIZATION_SETTINGS_SECTIONS.map(
  (section) =>
    createRoute({
      getParentRoute: () => settingsOrganizationLayoutRoute,
      path: section.id,
      component: () => (
        <SettingsOrganizationSectionPage sectionId={section.id} />
      ),
      loader: () => ({ crumb: section.title }),
      beforeLoad: async ({ context }) => {
        const isGentlyStaff = !!context.auth.currentUser?.isAdmin;
        // Integration Marketplace Demo (?demo=im): allow reaching the
        // gently-only Data Sources section in demo mode. Delete with
        // frontend/src/demo/im.
        if (section.gentlyOnly && !isGentlyStaff && !isImDemoMode()) {
          throw redirect({ to: sectionPath(DEFAULT_ORG_SECTION_ID) });
        }
        if (
          section.requiredModule &&
          !context.isModuleEnabled(section.requiredModule)
        ) {
          throw redirect({ to: sectionPath(DEFAULT_ORG_SECTION_ID) });
        }
        if (section.requiredPermission && !isGentlyStaff) {
          const allowed = await context.authzClient.can(
            section.requiredPermission.action,
            section.requiredPermission.subject,
          );
          if (!allowed) {
            throw redirect({ to: sectionPath(DEFAULT_ORG_SECTION_ID) });
          }
        }
      },
    }),
);

const settingsOrganizationEmailTemplateLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'email-templates/$templateId',
  component: Outlet,
  loader: () => ({ crumb: 'Email Template Editor' }),
  validateSearch: z.object({}),
});

const settingsOrganizationEmailTemplateEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationEmailTemplateLayoutRoute,
  path: '/',
  component: EmailTemplateEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationEmailTemplateVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationEmailTemplateLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/email-templates/EmailTemplateVersionView'),
    'EmailTemplateVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationEmailTemplateVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationEmailTemplateLayoutRoute,
  path: '/versions',
  component: EmailTemplateVersionsPage,
  loader: () => ({ crumb: 'Versions' }),
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'pdf-templates/$templateId',
  component: Outlet,
  loader: () => ({ crumb: 'PDF Template Editor' }),
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationPdfTemplateLayoutRoute,
  path: '/',
  component: PdfTemplateEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationPdfTemplateLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/pdf-templates/PdfTemplateVersionView'),
    'PdfTemplateVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationPdfTemplateVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationPdfTemplateLayoutRoute,
  path: '/versions',
  component: PdfTemplateVersionsPage,
  loader: () => ({ crumb: 'Versions' }),
  validateSearch: z.object({}),
});

const settingsOrganizationInternalUiTemplateLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'internal-ui-templates/$templateId',
  component: Outlet,
  loader: () => ({ crumb: 'Custom UI Editor' }),
  validateSearch: z.object({}),
});

const settingsOrganizationInternalUiTemplateEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationInternalUiTemplateLayoutRoute,
  path: '/',
  component: InternalUiTemplateEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationInternalUiTemplateVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationInternalUiTemplateLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/internal-ui-templates/InternalUiTemplateVersionView'),
    'InternalUiTemplateVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationInternalUiTemplateVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationInternalUiTemplateLayoutRoute,
  path: '/versions',
  component: InternalUiTemplateVersionsPage,
  loader: () => ({ crumb: 'Versions' }),
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockLayoutRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'template-blocks/$templateBlockId',
  component: Outlet,
  loader: () => ({ crumb: 'Template Block Editor' }),
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockEditorRoute = createRoute({
  getParentRoute: () => settingsOrganizationTemplateBlockLayoutRoute,
  path: '/',
  component: TemplateBlockEditorPage,
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockVersionRoute = createRoute({
  getParentRoute: () => settingsOrganizationTemplateBlockLayoutRoute,
  path: '/v/$versionNumber',
  component: lazyRouteComponent(
    () =>
      import('./pages/authenticated/settings/organization/components/cards/template-blocks/TemplateBlockVersionView'),
    'TemplateBlockVersionView',
  ),
  loader: ({ params }) => ({ crumb: `v${params.versionNumber}` }),
  validateSearch: z.object({}),
});

const settingsOrganizationTemplateBlockVersionsRoute = createRoute({
  getParentRoute: () => settingsOrganizationTemplateBlockLayoutRoute,
  path: '/versions',
  component: TemplateBlockVersionsPage,
  loader: () => ({ crumb: 'Versions' }),
  validateSearch: z.object({}),
});

const settingsOrganizationSchemaDefinitionDetailRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: 'schema-definitions/$schemaId',
  component: SchemaDefinitionDetailPage,
  loader: () => ({ crumb: 'Schema Definition' }),
  validateSearch: z.object({}),
});

// Legacy redirect: /settings/organization/<old-slug> → new path. Keeps
// bookmarks working after slug renames. Specific section routes above win
// over this dynamic match.
const settingsOrganizationLegacyRedirectRoute = createRoute({
  getParentRoute: () => settingsOrganizationLayoutRoute,
  path: '$section',
  beforeLoad: ({ params }) => {
    throw redirect({ to: sectionPath(resolveSectionId(params.section)) });
  },
});

const controlPlaneLinkRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/control-plane-link',
  component: ControlPlaneLinkPage,
  // Handoff/enrollment tokens are carried in the URL hash fragment (not the
  // query string) to keep them out of server logs, so no search params are
  // expected on this route.
  validateSearch: z.object({}),
  beforeLoad: ({ context }) => {
    if (!context.auth.currentUser?.isAdmin) {
      throw redirect({
        to: '/settings/organization/$section',
        params: { section: 'products' },
      });
    }
  },
  loader: () => ({
    crumb: 'Control Plane Link',
  }),
});

const settingsUserLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/user',
  component: UserPreferencesPage,
  loader: () => ({
    crumb: 'User Settings',
  }),
});

const settingsUserIndexRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/settings/user/security' });
  },
});

const settingsUserSecurityRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'security',
  component: SecurityPage,
  loader: () => ({
    crumb: 'Security',
  }),
});

const settingsUserOutOfOfficeRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'out-of-office',
  component: OutOfOfficePage,
  loader: () => ({
    crumb: 'Out of office',
  }),
});

const settingsUserNavigationRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'navigation',
  component: NavigationSettingsPage,
  loader: () => ({
    crumb: 'Navigation',
  }),
});

const settingsUserNotificationsRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'notifications',
  component: NotificationSettingsPage,
  loader: () => ({
    crumb: 'Notifications',
  }),
});

const settingsUserEmailSignatureRoute = createRoute({
  getParentRoute: () => settingsUserLayoutRoute,
  path: 'email-signature',
  component: EmailSignaturePage,
  loader: () => ({
    crumb: 'Email signature',
  }),
});

const enumAssociationsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/organization/enums/$enumId/associations',
  component: EnumAssociationsPage,
  loader: () => ({
    crumb: 'Enum Associations',
  }),
  beforeLoad: ({ context }) => {
    if (!context.auth.currentUser?.isAdmin) {
      throw redirect({ to: '/settings/organization/enums' });
    }
  },
});

const ExternalApisListPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/external-apis/ExternalApisListPage'),
  'ExternalApisListPage',
);
const ExternalApiDetailPage = lazyRouteComponent(
  () =>
    import('./pages/authenticated/settings/external-apis/ExternalApiDetailPage'),
  'ExternalApiDetailPage',
);

const externalApisListRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/external-apis',
  component: ExternalApisListPage,
  loader: () => ({
    crumb: 'External APIs',
  }),
});

const externalApiDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/settings/external-apis/$apiId',
  component: ExternalApiDetailPage,
  loader: () => ({
    crumb: 'API Detail',
  }),
});

const featureFlagsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ff_overrides',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: FeatureFlagsPage,
  loader: () => ({
    crumb: 'Feature Flags',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const historicalImportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/admin/historical-import',
  staticData: {
    app: {
      nav: {
        label: 'Historical Data Import',
        icon: <ClockCounterClockwiseIcon className="h-4 w-4" weight="fill" />,
        group: 'Rebates',
        order: 90,
      },
      access: {
        adminOnly: true,
        modulesAnyOf: [ProductModule.REBATES],
      },
    },
  },
  component: HistoricalImportPage,
  loader: () => ({
    crumb: 'Historical Import',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
    requireModules({
      context,
      oneOf: [ProductModule.REBATES],
    });
  },
});

const rexelMonitoringRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/rexel-monitoring',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: RexelMonitoringPage,
  loader: () => ({
    crumb: 'Rexel Monitor',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const backfillsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/admin/backfills',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: BackfillsPage,
  loader: () => ({
    crumb: 'Backfills',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const oauthAzureFlowCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth-azure-callback',
  component: AzureOauthFlowCallback,
  validateSearch: z.object({
    code: z.string().optional(),
    error: z.string().optional(),
    error_description: z.string().optional(),
  }),
});

const qaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/qa',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: QAPage,
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const statusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/status',
  component: StatusPage,
});

// Static landing for the Gently Teams app's personal-scope tab. Referenced
// from erp-teams-app/manifest-template/manifest.json as
// `staticTabs[0].contentUrl = "{{BASE_URL}}/teams/tab"`. Lives under the
// root (not pathlessAuthenticatedRoute) so Teams can load it inside the
// iframe without bouncing through auth.
const teamsTabRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/teams/tab',
  component: TeamsTabPage,
});

// ── AI-first Ship & Debit demo module ──────────────────────────────────────
// Frontend-only, mock-data work queues gated by the existing SHIP_AND_DEBIT
// module. Nav group "Ship & Debit" (the legacy "Ship and Debit" / "Ship and
// Debit Admin" groups above are hidden).
const SD_DEMO_GROUP = 'Ship & Debit';
const SD_DEMO_GROUP_ORDER = 59;

const sdDemoDashboardRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/dashboard',
  staticData: {
    app: {
      nav: {
        label: 'Dashboard',
        icon: <GaugeIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 10,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoDashboard,
  loader: () => ({ crumb: 'Ship & Debit' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoProgramsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/agreements',
  staticData: {
    app: {
      nav: {
        label: 'Agreements',
        icon: <FolderOpenIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 20,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoPrograms,
  loader: () => ({ crumb: 'Agreements' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoClaimsReviewRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/claims-review',
  staticData: {
    app: {
      nav: {
        label: 'Claims Review',
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 30,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoClaimsReview,
  loader: () => ({ crumb: 'Claims Review' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoClaimsSubmissionRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/claims-submission',
  staticData: {
    app: {
      nav: {
        label: 'Claims Submission',
        icon: <PaperPlaneTiltIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 50,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoClaimsSubmission,
  loader: () => ({ crumb: 'Claims Submission' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoVendorResponsesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/vendor-responses',
  staticData: {
    app: {
      nav: {
        label: 'Vendor Responses',
        icon: <ChatCenteredDotsIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 60,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoVendorResponses,
  loader: () => ({ crumb: 'Vendor Responses' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoCollectionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/collections',
  staticData: {
    app: {
      nav: {
        label: 'Collections & Aging',
        icon: <ClockCountdownIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 70,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoCollections,
  loader: () => ({ crumb: 'Collections & Aging' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoRevenueLeakageRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/revenue-leakage',
  staticData: {
    app: {
      nav: {
        label: 'Revenue Leakage',
        icon: <TrendDownIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 80,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoRevenueLeakage,
  loader: () => ({ crumb: 'Revenue Leakage' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const sdDemoVendorManagementRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/vendors',
  staticData: {
    app: {
      nav: {
        label: 'Vendor Management',
        icon: <HandshakeIcon className="h-4 w-4" weight="fill" />,
        group: SD_DEMO_GROUP,
        order: 100,
        groupOrder: SD_DEMO_GROUP_ORDER,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SdDemoVendorManagement,
  loader: () => ({ crumb: 'Vendor Management' }),
  beforeLoad: ({ context }) => {
    requireModules({ context, oneOf: [ProductModule.SHIP_AND_DEBIT] });
  },
});

const shipAndDebitExecutiveSummaryRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/executive-summary',
  staticData: {
    app: {
      nav: {
        label: 'Executive Summary',
        icon: <PresentationChartIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        groupOrder: 61,
        order: 5,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: ExecutiveSummaryPage,
  loader: () => ({
    crumb: 'Executive Summary',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const shipAndDebitDashboardRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit',
  staticData: {
    app: {
      nav: {
        label: 'Pending Transactions',
        icon: <ArrowsLeftRightIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 10,
        groupOrder: 60,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: ShipAndDebitDashboard,
  validateSearch: z.object({
    filters: z.record(z.string(), z.array(z.string())).optional(),
    search: z.string().optional(),
    sortBy: z.string().optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
    transactionId: z.string().optional(),
    view: z.enum(['as400', 'v2']).optional(),
    page: z.number().int().min(1).optional(),
    pageSize: z.number().optional(),
  }),
  loader: () => ({
    crumb: 'Ship and Debit',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDTransaction,
    });
  },
});

// Redirect old URL to new one
const shipAndDebitStaged844RedirectRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/staged-844',
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/ship-and-debit/staged',
      search: search as Record<string, unknown>,
    });
  },
});

const shipAndDebitStaged844Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/staged',
  validateSearch: z.object({
    filters: z.record(z.string(), z.array(z.string())).optional(),
    transactionId: z.string().optional(),
    transactionIds: z.string().optional(),
    view: z.enum(['as400', 'v2']).optional(),
    page: z.number().int().min(1).optional(),
  }),
  staticData: {
    app: {
      nav: {
        label: 'Staged Transactions',
        icon: <ArrowsLeftRightIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 15,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: Staged844TransactionsPage,
  loader: () => ({
    crumb: 'Staged Transactions',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDTransaction,
    });
  },
});

const shipAndDebitNoClaimRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/no-claim-transactions',
  validateSearch: z.object({
    filters: z.record(z.string(), z.array(z.string())).optional(),
    search: z.string().optional(),
    transactionId: z.string().optional(),
    view: z.enum(['as400', 'v2']).optional(),
    page: z.number().int().min(1).optional(),
  }),
  staticData: {
    app: {
      nav: {
        label: 'No Claim Transactions',
        icon: <TrashIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 56,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: NoClaimTransactionsPage,
  loader: () => ({
    crumb: 'No Claim Transactions',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDTransaction,
    });
  },
});

const shipAndDebitVDMsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/vdm',
  staticData: {
    app: {
      nav: {
        label: 'VDMs',
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.VDM }],
      },
    },
  },
  component: VDMsPage,
  loader: () => ({
    crumb: 'VDMs',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.VDM,
    });
  },
  validateSearch: z.object({
    status: z.array(z.string()).optional(),
    supplierId: z.string().optional(),
    companyCode: z.string().optional(),
    divisionCode: z.string().optional(),
    search: z.string().optional(),
    createdAtFrom: z.string().optional(),
    createdAtTo: z.string().optional(),
    sortBy: z.string().optional(),
    sortOrder: z.enum(['ASC', 'DESC']).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
});

const shipAndDebitVDMDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/vdm/$vdmId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.VDM }],
      },
    },
  },
  component: VDMDetailPage,
  loader: () => ({
    crumb: 'VDM Detail',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.VDM,
    });
  },
});

const shipAndDebitStagedInternalRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/staged-internal',
  validateSearch: z.object({
    filters: z.record(z.string(), z.array(z.string())).optional(),
    transactionId: z.string().optional(),
    view: z.enum(['as400', 'v2']).optional(),
    page: z.number().int().min(1).optional(),
  }),
  staticData: {
    app: {
      nav: {
        label: 'Staged Internal Transactions',
        icon: <ArrowsLeftRightIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 16,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: StagedInternalTransactionsPage,
  loader: () => ({
    crumb: 'Staged Internal Transactions',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDTransaction,
    });
  },
});

const shipAndDebitSearchTransactionsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/search-transactions',
  validateSearch: z.object({
    divisions: z.array(z.string()).optional(),
    branches: z.array(z.string()).optional(),
    suppliers: z.array(z.string()).optional(),
    customer: z.string().optional(),
    invoiceNo: z.string().optional(),
    orderLineNo: z.string().optional(),
    productCode: z.string().optional(),
  }),
  staticData: {
    app: {
      nav: {
        label: 'Search Transactions',
        icon: <MagnifyingGlassIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 17,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: SearchTransactionsPage,
  loader: () => ({
    crumb: 'Search Transactions',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDTransaction,
    });
  },
});

const shipAndDebit844And849Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/844-submissions',
  staticData: {
    app: {
      nav: {
        label: '844 Submissions',
        icon: <ArrowsLeftRightIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SDTransaction },
        ],
      },
    },
  },
  component: ShipAndDebit844sPage,
  loader: () => ({
    crumb: '844 Submissions',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDTransaction,
    });
  },
  validateSearch: z.object({
    page: z.number().int().min(0).optional(),
    pageSize: z.number().int().optional().catch(undefined),
    submissionId: z.string().optional(),
    status: z.string().optional(),
    suppliers: z.array(z.string()).optional(),
    divisions: z.array(z.string()).optional(),
    sentDateFrom: z.string().optional(),
    sentDateTo: z.string().optional(),
    // Mirror the 844 RPC contract shapes so invalid URL params are rejected at
    // route parsing instead of being client-accepted then server-rejected.
    batchNumber: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
    deliveryMethod: z.enum(['edi', 'email']).optional(),
    submissionNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
    vdmNumbers: z.array(z.string().regex(/^\d+(-\d+)?$/)).optional(),
  }),
});

const shipAndDebit849ResponsesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/849-responses',
  staticData: {
    app: {
      nav: {
        label: '849 Responses',
        icon: <EnvelopeSimpleIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SD849Response },
        ],
      },
    },
  },
  component: ShipAndDebit849ResponsesPage,
  loader: () => ({
    crumb: '849 Responses',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SD849Response,
    });
  },
  validateSearch: z.object({
    page: z.number().int().min(0).optional(),
    pageSize: z.number().int().optional().catch(undefined),
    suppliers: z.array(z.string()).optional(),
    statuses: z
      .array(
        z.enum([
          'all',
          'accepted',
          'rejected',
          'partial',
          'unknown',
          'resubmitted',
        ]),
      )
      .optional()
      .catch(undefined),
    sourceTypes: z
      .array(z.enum(['email', 'edi']))
      .optional()
      .catch(undefined),
    processedAtFrom: z.string().optional(),
    processedAtTo: z.string().optional(),
    batchNumber: z.array(z.string()).optional(),
    divisions: z.array(z.string()).optional(),
  }),
});

const shipAndDebit849ResponseDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/849-responses/$responseId',
  staticData: {
    app: {
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SD849Response },
        ],
      },
    },
  },
  component: ShipAndDebit849ResponseDetailPage,
  validateSearch: z.object({
    lineIndex: z.coerce.number().int().min(0).optional(),
  }),
  loader: () => ({
    crumb: '849 Response Detail',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SD849Response,
    });
  },
});

const shipAndDebitSearch844LegacyRedirect = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/search-844',
  beforeLoad: () => {
    throw redirect({ to: '/ship-and-debit/search-rexel-files', search: true });
  },
});

const shipAndDebitSearch844Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/search-rexel-files',
  staticData: {
    app: {
      nav: {
        label: 'Search Rexel Files',
        icon: <ArrowsLeftRightIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: Search844Page,
  validateSearch: z.object({
    fileType: z
      .enum(['844sd', '844d', 'itemast', 'invmas', 'vendr1', 'orderdad'])
      .optional(),
    source: z.enum(['azure', 'database']).optional(),
    company: z.string().optional(),
    division: z.string().optional(),
    branch: z.string().optional(),
    tradingPartner: z.string().optional(),
    invoiceNumber: z.string().optional(),
    orderNumber: z.string().optional(),
    claimNumber: z.string().optional(),
    customUse: z.string().optional(),
    supplierNumber: z.string().optional(),
    vend: z.string().optional(),
    location: z.string().optional(),
    itemNumber: z.string().optional(),
    fileDate: z.string().optional(),
    filters: z.record(z.string(), z.string()).optional(),
    limit: z.number().optional(),
    offset: z.number().optional(),
  }),
  loader: () => ({
    crumb: 'Search Rexel Files',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const shipAndDebitActivityLogRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/activity-log',
  staticData: {
    app: {
      nav: {
        label: 'Activity Log',
        icon: <ClockCounterClockwiseIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: ActivityLogPage,
  loader: () => ({
    crumb: 'Activity Log',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const shipAndDebitCompanyLocationsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/company-locations',
  staticData: {
    app: {
      nav: {
        label: 'Company Locations',
        icon: <BuildingIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: CompanyLocationsPage,
  loader: () => ({
    crumb: 'Company Locations',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const shipAndDebitRexelFilesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/rexel-files',
  staticData: {
    app: {
      nav: {
        label: 'Rexel Files',
        icon: <TableIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 35,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: RexelFilesPage,
  validateSearch: z.object({}),
  loader: () => ({
    crumb: 'Rexel Files',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const shipAndDebitAdminReportsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/reports',
  staticData: {
    app: {
      nav: {
        label: 'Reports',
        icon: <ChartBarIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 50,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: AdminReportsPage,
  loader: () => ({
    crumb: 'Reports',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const shipAndDebitAgentConfigRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/ship-and-debit/agent-config',
  staticData: {
    app: {
      nav: {
        label: 'SD Agent',
        icon: <RobotIcon className="h-4 w-4" weight="fill" />,
        group: 'Ship and Debit Admin',
        // Hidden: superseded by the new AI-first Ship & Debit demo module.
        hidden: true,
        order: 55,
      },
      access: {
        modulesAnyOf: [ProductModule.SHIP_AND_DEBIT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.SDAdmin }],
      },
    },
  },
  component: AgentConfigPage,
  loader: () => ({
    crumb: 'SD Agent',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.SHIP_AND_DEBIT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SDAdmin,
    });
  },
});

const backgroundAgentsLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agents',
  staticData: {
    app: {
      access: {
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.BackgroundAgent },
        ],
      },
    },
  },
  component: BackgroundAgentsPage,
  loader: () => ({
    crumb: 'Background Agents',
  }),
  beforeLoad: async ({ context }) => {
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.BackgroundAgent,
      redirectTo: '/',
    });
  },
  validateSearch: z.object({}),
});

// Child routes intentionally omit `staticData.app.access`.
// The parent `/agents` route enforces RBAC for the entire subtree.
const backgroundAgentsListingRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: '/',
  component: BackgroundAgentsListingPage,
  loader: () => ({
    crumb: 'Listing',
  }),
  validateSearch: z.object({}),
});

const backgroundAgentTemplatesRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: 'templates',
  component: BackgroundAgentTemplatesPage,
  loader: () => ({
    crumb: 'Templates',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionLayoutRoute = createRoute({
  getParentRoute: () => backgroundAgentsLayoutRoute,
  path: '/$agentDefinitionId',
  component: AgentDefinitionLayoutPage,
  loader: async ({ context, params }) => {
    const { queryClient } = context;

    const definition = await queryClient.ensureQueryData(
      orpc.agentDefinitions.get.queryOptions({
        input: {
          agentDefinitionId: params.agentDefinitionId,
        },
      }),
    );

    const crumb = definition?.name
      ? `Agent: ${definition?.name}`
      : 'Agent Definition';
    return {
      crumb,
    };
  },
  validateSearch: z.object({}),
});

const agentDefinitionAgentTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/',
  component: AgentDefinitionAgentTab,
  validateSearch: z.object({}),
});

const agentDefinitionToolsTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/tools',
  component: AgentDefinitionToolsTab,
  loader: () => ({
    crumb: 'Tools',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionTaskManagementTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/task-management',
  component: AgentDefinitionTaskManagementTab,
  loader: () => ({
    crumb: 'Task Management',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionResourcesTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/resources',
  component: AgentDefinitionResourcesTab,
  loader: () => ({
    crumb: 'Resources',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionPermissionsTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/permissions',
  component: AgentDefinitionPermissionsTab,
  loader: () => ({
    crumb: 'Permissions',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionMaterializeTabRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/materialize',
  component: AgentDefinitionMaterializeTab,
  loader: () => ({
    crumb: 'Materialize',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionSessionsLayoutRoute = createRoute({
  getParentRoute: () => agentDefinitionLayoutRoute,
  path: '/sessions',
  component: AgentDefinitionSessionsLayout,
  loader: () => ({
    crumb: 'Sessions',
  }),
  validateSearch: z.object({}),
});

const agentDefinitionSessionsListRoute = createRoute({
  getParentRoute: () => agentDefinitionSessionsLayoutRoute,
  path: '/',
  component: AgentDefinitionSessionsTab,
  validateSearch: z.object({}),
});

const agentRunDetailRoute = createRoute({
  getParentRoute: () => agentDefinitionSessionsLayoutRoute,
  path: '/$agentRunId',
  component: AgentRunDetailPage,
  loader: () => ({
    crumb: 'Run Detail',
  }),
  validateSearch: z.object({}),
});

// 1) Layout parent: shows in matches, provides breadcrumb, renders WorkflowsPage with tabs
const workflowsListLayoutRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/workflows',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowsPage,
  loader: () => ({
    crumb: 'Workflows',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const workflowDefinitionsTabRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: '/',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    mine: z.coerce.boolean().optional(),
    published: z.coerce.boolean().optional(),
    paused: z.coerce.boolean().optional(),
    scheduled: z.coerce.boolean().optional(),
    event: z.coerce.boolean().optional(),
    deleted: z.coerce.boolean().optional(),
    tags: z.array(z.string()).optional(),
    sortBy: z.enum(['name', 'createdAt', 'updatedAt']).optional(),
    sortDirection: z.enum(['asc', 'desc']).optional(),
  }),
  component: WorkflowDefinitionsTab,
});

const workflowGroupsTabRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'groups',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowGroupsTab,
  loader: () => ({
    crumb: 'Workflow Groups',
  }),
});

const workflowGroupDetailRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'groups/$workflowGroupId',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowGroupEditorPage,
  loader: async ({ context, params }) => {
    if (params.workflowGroupId === 'new') {
      return {
        crumb: 'New Workflow Group',
      };
    }

    try {
      const group = await context.queryClient.ensureQueryData(
        orpc.workflows.getGroup.queryOptions({
          input: {
            workflowGroupId: params.workflowGroupId,
          },
        }),
      );

      return {
        crumb: group.name || 'Workflow Group',
      };
    } catch {
      return {
        crumb: 'Workflow Group',
      };
    }
  },
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

// Templates tab (renders at /workflows/templates)
const workflowTemplatesRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'templates',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowTemplatesPage,
  loader: () => ({
    crumb: 'Templates',
  }),
});

// Step Tester tab (renders at /workflows/step-tester)
const workflowStepTesterRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'step-tester',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowStepTesterPage,
  loader: () => ({
    crumb: 'Step Tester',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

// Traffic Generator tab (renders at /workflows/traffic-generator)
const workflowTrafficGeneratorRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'traffic-generator',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowTrafficGeneratorPage,
  loader: () => ({
    crumb: 'Traffic Generator',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const workflowDataAssetsRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'data-assets',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: DataAssetsPage,
  loader: () => ({
    crumb: 'Data Assets',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const workflowStatefulTablesRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'stateful-tables',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: StatefulTablesPage,
  loader: () => ({
    crumb: 'Stateful Tables',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

const workflowStatefulTableDetailRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: 'stateful-tables/$tableId',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: StatefulTableDetailPage,
  loader: () => ({
    crumb: 'Stateful Table',
  }),
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

// ---------------------------------------------------------------------------
// Workflow viewer
//
// The URL identity is the Workflow (lineage), not a specific version. Without
// a version path segment the viewer shows the pinned definition; `/v/N`
// swaps to a specific version. Every inner tab reads the resolved `workflow`
// and `definition` off the matching layout route's loader data via
// `useWorkflowViewerContext()` — they never re-read the URL param.
// ---------------------------------------------------------------------------

/**
 * Shared resolver for both the pinned-view and version-view layout loaders.
 * Accepts either a concrete version number (for `/v/$n`) or null (pinned).
 * Returns loader data shared by both: the workflow, the resolved definition,
 * and the version number currently being viewed (or `null` when the workflow
 * has no pinned version yet).
 */
async function loadWorkflowViewerData({
  queryClient,
  workflowId,
  viewingVersion,
}: {
  queryClient: ReturnType<
    typeof import('@tanstack/react-query').useQueryClient
  >;
  workflowId: string;
  viewingVersion: number | null;
}) {
  const workflow = await queryClient.ensureQueryData(
    orpc.workflows.getWorkflow.queryOptions({
      input: { workflowId },
    }),
  );

  let targetDefinitionId: string | null = null;
  let resolvedVersion: number | null = null;

  if (viewingVersion !== null) {
    const versions = await queryClient.ensureQueryData(
      orpc.workflows.listVersions.queryOptions({
        input: { workflowId },
      }),
    );
    const match = versions.versions.find((v) => v.version === viewingVersion);
    if (match) {
      targetDefinitionId = match.id;
      resolvedVersion = match.version;
    }
  } else if (workflow.pinnedVersionId) {
    targetDefinitionId = workflow.pinnedVersionId;
  } else {
    // No pinned version — fall back to the latest version (e.g. an unpinned
    // draft created via "Save Draft" before the first "Create").
    const versions = await queryClient.ensureQueryData(
      orpc.workflows.listVersions.queryOptions({
        input: { workflowId },
      }),
    );
    const latest = versions.versions[0];
    if (latest) {
      targetDefinitionId = latest.id;
      resolvedVersion = latest.version;
    }
  }

  const definition = targetDefinitionId
    ? await queryClient.ensureQueryData(
        orpc.workflows.getDefinition.queryOptions({
          input: { workflowDefinitionId: targetDefinitionId },
        }),
      )
    : null;

  if (definition && resolvedVersion === null) {
    resolvedVersion = definition.version;
  }

  return { workflow, definition, viewingVersionNumber: resolvedVersion };
}

// Layout route for the pinned view (`/workflows/$workflowId`).
const workflowLayoutRoute = createRoute({
  getParentRoute: () => workflowsListLayoutRoute,
  path: '/$workflowId',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowDefinitionLayoutPage,
  loader: async ({ context, params }) => {
    // "new" is the create-workflow sentinel — the editor renders without
    // pre-loaded data in that case.
    if (params.workflowId === 'new') {
      return {
        crumb: 'New Workflow',
        workflow: null,
        definition: null,
        viewingVersionNumber: null,
      };
    }

    const data = await loadWorkflowViewerData({
      queryClient: context.queryClient,
      workflowId: params.workflowId,
      viewingVersion: null,
    });

    return {
      crumb: data.workflow?.name
        ? `Workflow: ${data.workflow.name}`
        : 'Workflow',
      ...data,
    };
  },
  beforeLoad: ({ context }) => {
    requireAdmin({ context });
  },
});

// Layout route for a specific version view (`/workflows/$workflowId/v/$n`).
// Nested under the pinned layout: chrome (header + tabs) is rendered by the
// outer layout, which reads whichever inner loader's data exists via
// `useWorkflowViewerContext`. This route is a transparent pass-through — its
// own component is just `<Outlet />` so tab children render in place without
// drawing the header a second time.
const workflowVersionLayoutRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/v/$versionNumber',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: Outlet,
  loader: async ({ context, params }) => {
    const parsed = Number.parseInt(params.versionNumber, 10);
    if (!Number.isFinite(parsed)) {
      return {
        crumb: 'Version',
        workflow: null,
        definition: null,
        viewingVersionNumber: null,
      };
    }

    const data = await loadWorkflowViewerData({
      queryClient: context.queryClient,
      workflowId: params.workflowId,
      viewingVersion: parsed,
    });

    return {
      crumb: `v${parsed}`,
      ...data,
    };
  },
});

// Tab routes — duplicated once for the pinned layout and once for the
// version layout so that both URL shapes expose the same tabbed UI. Each
// component reads `workflow` / `definition` / `viewingVersionNumber` from
// the nearest layout's loader data via `useWorkflowViewerContext()`.

const workflowDefinitionOverviewRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionOverviewTab,
  validateSearch: z.object({
    edit: z.boolean().optional(),
    /** Full-screen read-only graph viewer (post-publish style, no edit bootstrap). */
    view: z.literal('graph').optional(),
    emailRuleId: z.string().uuid().optional(),
    triggerType: z
      .enum([
        'schedule_trigger',
        'event_trigger',
        'email_rule_trigger',
        'http_trigger',
        'action_input',
        'none',
      ])
      .optional(),
  }),
});

const workflowVersionOverviewRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionOverviewTab,
  validateSearch: z.object({
    edit: z.boolean().optional(),
    /** Full-screen read-only graph viewer (post-publish style, no edit bootstrap). */
    view: z.literal('graph').optional(),
    emailRuleId: z.string().uuid().optional(),
    /** Step to focus when opening the editor from overview (step id in graph) */
    stepId: z.string().optional(),
  }),
});

const workflowDefinitionStepsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/steps',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionStepsTab,
  loader: () => ({ crumb: 'Steps' }),
});

const workflowVersionStepsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/steps',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionStepsTab,
  loader: () => ({ crumb: 'Steps' }),
});

const workflowDefinitionRunsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/runs',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionRunsTab,
  loader: () => ({ crumb: 'Runs' }),
});

const workflowVersionRunsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/runs',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionRunsTab,
  loader: () => ({ crumb: 'Runs' }),
});

const workflowDefinitionVersionsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/versions',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionVersionsTab,
  loader: () => ({ crumb: 'Versions' }),
});

const workflowVersionVersionsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/versions',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionVersionsTab,
  loader: () => ({ crumb: 'Versions' }),
});

const workflowDefinitionTestRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/test',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionTestTab,
  loader: () => ({ crumb: 'Test' }),
});

const workflowVersionTestRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/test',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowDefinitionTestTab,
  loader: () => ({ crumb: 'Test' }),
});

const workflowDefinitionAnalyticsRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/analytics',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowAnalyticsTab,
  loader: () => ({ crumb: 'Analytics' }),
});

const workflowVersionAnalyticsRoute = createRoute({
  getParentRoute: () => workflowVersionLayoutRoute,
  path: '/analytics',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowAnalyticsTab,
  loader: () => ({ crumb: 'Analytics' }),
});

// HTTP API tab is lineage-scoped (trigger tokens bind to a Workflow lineage,
// not a definition version), so we only mount it on the pinned layout — there's no
// useful version-scoped variant. Links from the version layout fall through
// to the pinned URL via `paths.authenticated.workflows.workflow`.
const workflowDefinitionApiTokensRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/tokens',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowApiTokensTab,
  loader: () => ({ crumb: 'HTTP API' }),
});

const workflowManagerAgentRoute = createRoute({
  getParentRoute: () => workflowLayoutRoute,
  path: '/manager',
  staticData: { app: { access: { adminOnly: true } } },
  component: WorkflowManagerAgentTab,
  loader: () => ({ crumb: 'Manager' }),
});

// Run detail is version-agnostic (runs belong to the workflow, their own id
// identifies the exact definition version that ran). Nested under the runs
// route so the runs table stays visible as a sidebar when viewing a run.
const workflowRunDetailRoute = createRoute({
  getParentRoute: () => workflowDefinitionRunsRoute,
  path: '/$workflowRunId',
  staticData: {
    app: {
      access: {
        adminOnly: true,
      },
    },
  },
  component: WorkflowRunDetailPage,
  loader: async ({ context, params }) => {
    const { queryClient } = context;
    const run = await queryClient.ensureQueryData(
      orpc.workflows.getRun.queryOptions({
        input: {
          workflowRunId: params.workflowRunId,
        },
      }),
    );

    return {
      crumb: `Run ${run.id.slice(0, 8)}...`,
    };
  },
});

const productMarginsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/product-margins',
  staticData: {
    app: {
      nav: {
        label: 'Product Margins',
        icon: <PercentIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 25,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        // Gated by the Read:ProductMargins RBAC permission (Sales › Orders).
        // Replaces the legacy order-management.hasProductMargins org flag.
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ProductMargins },
        ],
      },
    },
  },
  component: ProductMarginsPage,
  loader: () => ({
    crumb: 'Product Margins',
  }),
  // Persist the simulated order (customer + lines) in the URL so it can be
  // bookmarked / shared / survive refresh. Lines are stored compactly as
  // { v: variantId, p: unitPrice, q: quantity }. `.catch` keeps a malformed
  // param (e.g. a cleared `customerId=`) from crashing the page.
  validateSearch: z.object({
    customerId: z.string().uuid().optional().catch(undefined),
    lines: z
      .array(
        z.object({
          v: z.string().uuid(),
          p: z.number(),
          q: z.number(),
        }),
      )
      .optional()
      .catch(undefined),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
});

const productCatalogRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/product-catalog',
  staticData: {
    app: {
      nav: {
        label: 'Product Catalog',
        icon: <PackageIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 26,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        // Gated by the Read:ProductCatalog RBAC permission (Sales › Orders),
        // independent of Product Margins so pricing visibility is grantable
        // on its own.
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.ProductCatalog },
        ],
      },
    },
  },
  component: ProductCatalogPage,
  loader: () => ({
    crumb: 'Product Catalog',
  }),
  // Pagination + filters live in the URL so a refresh / shared link restores
  // the full view. Each field `.catch(undefined)` so a malformed param can't
  // crash the page.
  validateSearch: z.object({
    page: z.number().int().min(1).optional().catch(undefined),
    pageSize: z.number().int().min(1).max(200).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    family: z.string().optional().catch(undefined),
    group: z.string().optional().catch(undefined),
    model: z.string().optional().catch(undefined),
    // Multi-selects are stored comma-separated for clean URLs (vs JSON arrays,
    // which serialize as %5B%22..%22%5D). The page splits/joins at the boundary.
    customerId: z.string().optional().catch(undefined),
    priceListId: z.string().optional().catch(undefined),
    noPriceList: z.boolean().optional().catch(undefined),
    status: z.enum(['active', 'inactive', 'all']).optional().catch(undefined),
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.ProductCatalog,
    });
  },
});

const customerProductMatchesRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/customer-product-matches',
  staticData: {
    app: {
      nav: {
        label: 'Product Matches',
        icon: <ClipboardTextIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 30,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.Customer },
          { action: Action.Read, subject: Subject.SalesOrder },
        ],
      },
    },
  },
  component: CustomerProductMatchesPage,
  loader: () => ({
    crumb: 'Customer Product Matches',
  }),
  validateSearch: z.object({
    customerId: z.string().optional(),
    productId: z.string().optional(),
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
  },
});

const discountsRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/discounts',
  staticData: {
    app: {
      nav: {
        label: 'Discounts',
        icon: <TagIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.Discount }],
      },
    },
  },
  component: DiscountsPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    sku?: string;
    customerId?: string;
    customerName?: string;
    tab?: string;
  } => ({
    sku: typeof search.sku === 'string' ? search.sku : undefined,
    customerId:
      typeof search.customerId === 'string' ? search.customerId : undefined,
    customerName:
      typeof search.customerName === 'string' ? search.customerName : undefined,
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  loader: () => ({
    crumb: 'Discounts',
  }),
  beforeLoad: async ({ context }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.Discount,
    });
  },
});

// Shared report page (accessed via shareable links)
const sharedReportRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/shared/$token',
  component: SharedReportPage,
  loader: () => ({
    crumb: 'Shared Report',
  }),
});

// Agent Monitor — user-facing, feature-flag gated (no requireAdmin)
const agentMonitorListingRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agent-monitor',
  staticData: {
    app: {
      nav: {
        label: 'Agent Monitor',
        icon: <MonitorIcon className="h-4 w-4" weight="fill" />,
        group: 'Overview',
        order: 10, // Inbox is order 5 → Agent Monitor lands directly under it
        groupOrder: 1,
        pinned: true,
      },
      access: {
        modulesAnyOf: [ProductModule.AP_INVOICING], //todo: revisit this
        featureFlag: 'agent-monitor-enabled',
        permissionsAnyOf: [{ action: Action.Read, subject: Subject.APInvoice }],
      },
    },
  },
  component: AgentMonitorListingPage,
  loader: () => ({
    crumb: 'Agent Monitor',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: AGENTS_ROUTE_MODULES,
    });
  },
  validateSearch: z.object({}),
});

const agentMonitorDetailRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/agent-monitor/$agentDefinitionId',
  component: AgentMonitorPage,
  loader: () => ({
    crumb: 'Agent Detail',
  }),
  beforeLoad: ({ context }) => {
    requireModules({
      context,
      oneOf: AGENTS_ROUTE_MODULES,
    });
  },
  validateSearch: z.object({}),
});

// Shared links management page
const sharedLinksRoute = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/shared-links',
  component: SharedLinksPage,
  loader: () => ({
    crumb: 'Shared Links',
  }),
  validateSearch: z.object({
    tab: z.enum(['my-links', 'shared-with-me']).optional().prefault('my-links'),
  }),
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  workosCallbackRoute,
  googleCallbackRoute,
  qaRoute,
  statusRoute,
  teamsTabRoute,
  oauthAzureFlowCallbackRoute,
  // Demo customer portal — its own gated tree with local/fake auth.
  portalLoginRoute,
  portalAuthenticatedRoute.addChildren([portalDashboardRoute]),
  pathlessAuthenticatedRoute.addChildren([
    inboxRoute,
    customersRoute,
    salesOrdersRoute,
    salesOrderRoute,
    enhancedSalesOrderDraftPageRoute,
    salesOrdersV3Route,
    salesOrderV3DetailRoute,
    salesOrdersV100Route,
    salesOrderV100BuildRoute,
    salesOrderV100DetailRoute,
    suppliersRoute,
    supplierDetailRoute.addChildren(supplierDetailTabRoutes),
    companiesRoute.addChildren([
      companyContactsListRoute,
      companyDetailRoute.addChildren(companyDetailTabRoutes),
    ]),
    invoiceDemosRoute, // TODO remove this demo route
    internalUiDemoRoute,
    rebateSupplierDetailRoute.addChildren(rebateSupplierDetailTabRoutes),
    orderMetricsRoute,
    salesReportsRoute,
    famousEDIRoute,
    famousEDIShipmentsRoute.addChildren([famousEDIShipmentDetailRoute]),
    famousEDIOrderConfirmationRoute.addChildren([
      famousEDIOrderConfirmationDetailRoute,
    ]),
    famousEdi855Route,
    ediTextMessagesRoute,
    // rebatesSubmittedRoute,
    rebatesForecastingRoute,
    rebateDemoAnalyticsRoute,
    rebatesDebtorsRoute,
    rebatesTierProximityRoute,
    rebatesAuditsRoute.addChildren([rebatesAuditDetailRoute]),
    rebatesReportsIndexRoute.addChildren([
      rebatesDivisionSummaryRoute,
      rebatesSupplierTagSummaryRoute,
      rebatesDailyEarningsPYERoute,
      rebatesMonthlyTrendingRoute,
      rebatesSupplierPaymentTermsRoute,
      rebatesProfitabilityReportRoute,
      rebatesSupplierYoYRoute,
      rebatesBudgetComparisonRoute,
    ]),
    rebatesDataExportRoute,
    rebatesSettingsRoute,
    rebatesVendorFollowupsRoute,
    // rebatesReconciliationRoute,
    rebatesAgreementsRoute.addChildren([
      rebatesAgreementLayoutRoute.addChildren([
        rebatesAgreementIndexRoute,
        rebatesAgreementSlugRoute,
        rebatesAgreementCreateVersionRoute,
        rebateCalculationAuditRoute,
      ]),
    ]),
    rebatesSupplierCollectionsRoute,
    rebatesSupplierReportedAmountsRoute,
    rebatesUploadDataRoute.addChildren([
      rebatesUploadDataAIRoute,
      rebatesUploadDataManualRoute,
      rebatesUploadDataJobsRoute,
      rebatesUploadDataRunRoute,
    ]),
    rebatesUploadDataDemoResultsRoute,
    rebatesUploadDataEmailQueueRoute,
    priceQuoteSheetDashboardRoute.addChildren([
      priceQuoteSheetDashboardDetailRoute,
    ]),
    priceQuoteSheetInboxRoute.addChildren([priceQuoteSheetInboxDetailRoute]),
    warehousesRoute.addChildren([warehousesIndexRoute, warehouseDetailRoute]),
    itemCatalogRoute,
    // Integration Marketplace Demo (?demo=im). Delete with frontend/src/demo/im.
    imWorkflowBuilderRoute,
    reviewsRoute,
    reviewDetailRoute,
    pricelistManagementRoute,
    replenishmentRoute,
    replenishmentSupplierDetailRoute,
    replenishmentOrdersRoute,
    salesOrderStatusRoute,
    transferOrderStatusRoute,
    as400RawDataRoute,
    queryBuilderRoute,
    queryBuilderPrintRoute,
    obsoleteProductsRoute,
    demandRadarRoute.addChildren([
      demandRadarIndexRoute,
      demandRadarItemDetailRoute,
    ]),
    procDemoOverviewRoute,
    procDemoRequestsRoute,
    procDemoSuppliersRoute.addChildren([
      procDemoSuppliersIndexRoute,
      procDemoSupplierDetailRoute,
      procDemoOnboardingCaseRoute,
    ]),
    procDemoMaterialRoute,
    procDemoSourcingRoute.addChildren([
      procDemoSourcingIndexRoute,
      procDemoSourcingEventRoute,
    ]),
    procDemoContractsRoute.addChildren([
      procDemoContractsIndexRoute,
      procDemoContractDetailRoute,
    ]),
    procDemoForecastSummaryRoute,
    procDemoForecastDetailsRoute,
    procDemoBomForecastRoute,
    procDemoReplenishmentRoute.addChildren([
      procDemoReplenishmentIndexRoute,
      procDemoReplenishmentItemRoute,
    ]),
    procDemoBuyerBoardRoute,
    procDemoInvoiceComplianceRoute,
    procDemoRiskRoute,
    procDemoAgentsRoute,
    procurementPurchaseOrdersRoute.addChildren([
      procurementPurchaseOrderDetailRoute,
    ]),
    inventoryRoute.addChildren([
      inventoryIndexRoute,
      inventoryProductDetailRoute,
    ]),
    supplierProductsRoute.addChildren([
      supplierProductsIndexRoute,
      supplierProductDetailRoute,
    ]),
    apInvoicesRoute.addChildren([apInvoiceDetailRoute]),
    apPurchaseOrdersRoute.addChildren([apPurchaseOrderDetailRoute]),
    apPaymentsRoute.addChildren([apPaymentDetailRoute]),
    arSummaryRoute,
    arIngestedSalesOrdersRoute.addChildren([arIngestedSalesOrderDetailRoute]),
    arCashReceiptsRoute.addChildren([arCashReceiptDetailRoute]),
    arPaymentsRoute.addChildren([arPaymentDetailRoute]),
    arRemittancesRoute.addChildren([arRemittanceDetailRoute]),
    arInvoicesRoute.addChildren([arInvoiceDetailRoute]),
    arReportsRoute,
    arNewReportRoute,
    arSavedReportRoute,
    arCollectionsRoute,
    arCustomerProfileRoute,
    arCollectionThreadDetailRoute,
    // AR Vision Demo (GEN-2247) — throwaway demo-only routes. Delete with src/demo/ar.
    arDemoRemindersRoute,
    demoArWorkflowRoute,
    // AP Invoices Demo — throwaway "build an AP workflow" canvas route.
    demoApWorkflowRoute,
    // AR Technical Demo (F4 / GEN-2260) — throwaway reconciliation route.
    arDemoReconciliationRoute,
    // AR Demo — throwaway blanket cash-receipt ingestion route.
    arDemoCashReceiptIngestionRoute,
    // AR Email Intelligence Demo (GEN-2263) — Scene 0 route. Delete with src/demo/ar.
    arIntelCorpusRoute,
    // AR Email Intelligence Demo (GEN-2264) — voice/draft hero route. Delete with src/demo/ar.
    arDemoVoiceIntelRoute,
    // AR Email Intelligence Demo (E3 / GEN-2265) — Customer Intelligence panel
    // route (Scenes 4–5). Delete with src/demo/ar/email-intel.
    arDemoIntelligenceRoute,
    // AR Email Intelligence Demo (GEN-2266) — throwaway demo-only payoff route.
    // Delete with frontend/src/demo/ar/email-intel.
    arDemoIntelSendRoute,
    divisionsRoute.addChildren([divisionRoute]),
    inboundEmailsRoute.addChildren([inboundEmailRulesRoute]),
    documentsRoute,
    documentRoute,
    fileExtractionRoute,
    settingsOrganizationLayoutRoute.addChildren([
      settingsOrganizationIndexRoute,
      settingsOrganizationEmailTemplateLayoutRoute.addChildren([
        settingsOrganizationEmailTemplateEditorRoute,
        settingsOrganizationEmailTemplateVersionRoute,
        settingsOrganizationEmailTemplateVersionsRoute,
      ]),
      settingsOrganizationPdfTemplateLayoutRoute.addChildren([
        settingsOrganizationPdfTemplateEditorRoute,
        settingsOrganizationPdfTemplateVersionRoute,
        settingsOrganizationPdfTemplateVersionsRoute,
      ]),
      settingsOrganizationInternalUiTemplateLayoutRoute.addChildren([
        settingsOrganizationInternalUiTemplateEditorRoute,
        settingsOrganizationInternalUiTemplateVersionRoute,
        settingsOrganizationInternalUiTemplateVersionsRoute,
      ]),
      settingsOrganizationTemplateBlockLayoutRoute.addChildren([
        settingsOrganizationTemplateBlockEditorRoute,
        settingsOrganizationTemplateBlockVersionRoute,
        settingsOrganizationTemplateBlockVersionsRoute,
      ]),
      settingsOrganizationSchemaDefinitionDetailRoute,
      ...settingsOrganizationSectionRoutes,
      settingsOrganizationLegacyRedirectRoute,
    ]),
    controlPlaneLinkRoute,
    settingsUserLayoutRoute.addChildren([
      settingsUserIndexRoute,
      settingsUserSecurityRoute,
      settingsUserOutOfOfficeRoute,
      settingsUserNavigationRoute,
      settingsUserNotificationsRoute,
      settingsUserEmailSignatureRoute,
    ]),
    welcomeRoute,
    tenantRoute,
    enumAssociationsRoute,
    externalApisListRoute,
    externalApiDetailRoute,
    featureFlagsRoute,
    historicalImportRoute,
    backfillsRoute,
    rexelMonitoringRoute,
    sdDemoDashboardRoute,
    sdDemoProgramsRoute,
    sdDemoClaimsReviewRoute,
    sdDemoClaimsSubmissionRoute,
    sdDemoVendorResponsesRoute,
    sdDemoCollectionsRoute,
    sdDemoRevenueLeakageRoute,
    sdDemoVendorManagementRoute,
    shipAndDebitExecutiveSummaryRoute,
    shipAndDebitDashboardRoute,
    shipAndDebitStaged844RedirectRoute,
    shipAndDebitStaged844Route,
    shipAndDebitStagedInternalRoute,
    shipAndDebitSearchTransactionsRoute,
    shipAndDebitNoClaimRoute,
    shipAndDebitVDMsRoute,
    shipAndDebitVDMDetailRoute,
    shipAndDebit844And849Route,
    shipAndDebit849ResponsesRoute,
    shipAndDebit849ResponseDetailRoute,
    shipAndDebitSearch844Route,
    shipAndDebitActivityLogRoute,
    shipAndDebitCompanyLocationsRoute,
    shipAndDebitRexelFilesRoute,
    shipAndDebitAdminReportsRoute,
    shipAndDebitAgentConfigRoute,
    shipAndDebitSearch844LegacyRedirect,
    memoryBankNotesRoute,
    guardrailPoliciesRoute,
    integrationsListRoute,
    integrationDetailRoute,
    auditReportsRoute,
    notificationsHistoryRoute,
    backgroundAgentsLayoutRoute.addChildren([
      backgroundAgentsListingRoute,
      backgroundAgentTemplatesRoute,
      agentDefinitionLayoutRoute.addChildren([
        agentDefinitionAgentTabRoute,
        agentDefinitionToolsTabRoute,
        agentDefinitionTaskManagementTabRoute,
        agentDefinitionResourcesTabRoute,
        agentDefinitionPermissionsTabRoute,
        agentDefinitionMaterializeTabRoute,
        agentDefinitionSessionsLayoutRoute.addChildren([
          agentDefinitionSessionsListRoute,
          agentRunDetailRoute,
        ]),
      ]),
    ]),
    workflowsListLayoutRoute.addChildren([
      workflowDefinitionsTabRoute,
      workflowGroupsTabRoute,
      workflowGroupDetailRoute,
      workflowTemplatesRoute,
      workflowStepTesterRoute,
      workflowTrafficGeneratorRoute,
      workflowDataAssetsRoute,
      workflowStatefulTablesRoute,
      workflowStatefulTableDetailRoute,
      workflowLayoutRoute.addChildren([
        workflowDefinitionOverviewRoute,
        workflowDefinitionStepsRoute,
        workflowDefinitionRunsRoute.addChildren([workflowRunDetailRoute]),
        workflowDefinitionVersionsRoute,
        workflowDefinitionTestRoute,
        workflowDefinitionAnalyticsRoute,
        workflowDefinitionApiTokensRoute,
        workflowManagerAgentRoute,
        workflowVersionLayoutRoute.addChildren([
          workflowVersionOverviewRoute,
          workflowVersionStepsRoute,
          workflowVersionRunsRoute,
          workflowVersionVersionsRoute,
          workflowVersionTestRoute,
          workflowVersionAnalyticsRoute,
        ]),
      ]),
    ]),

    productMarginsRoute,
    productCatalogRoute,
    customerProductMatchesRoute,
    discountsRoute,
    sharedReportRoute,
    sharedLinksRoute,
    agentMonitorListingRoute,
    agentMonitorDetailRoute,
  ]),
]);
